import { Dialog, Popover, Tooltip, Box, CircularProgress, Skeleton } from '@mui/material'
import ExpandMore from '@mui/icons-material/ExpandMore'
import ExpandLess from '@mui/icons-material/ExpandLess'
import FirstPage from '@mui/icons-material/FirstPage'
import AccessTime from '@mui/icons-material/AccessTime'
import SkipNext from '@mui/icons-material/SkipNext'
import { default as CloseIcon } from '@mui/icons-material/Close'
import dayjs from 'dayjs'
import utc from 'dayjs/plugin/utc'
import timezone from 'dayjs/plugin/timezone'
import isBetween from 'dayjs/plugin/isBetween'
import isSameOrBefore from 'dayjs/plugin/isSameOrBefore'
import isSameOrAfter from 'dayjs/plugin/isSameOrAfter'
import customParseFormat from 'dayjs/plugin/customParseFormat'
import React, { forwardRef, useCallback, useEffect, useImperativeHandle, useMemo, useRef, useState, Suspense, lazy } from 'react'
import { useNavigate, useLocation } from 'react-router-dom'
import { toast } from 'react-toastify'
import { getCPDataWithoutParams, getCPDataWithParams, putCPDataWithoutParams } from 'src/clinic/services'
import { getTimezoneOffset } from 'src/consumer/helpers/utilFunctions'
import { getSPDataWithParams, putSPDataWithParams } from 'src/supportPortal/services/services'
import { getDataMapFromCommonResponse, isCommonResponseSuccessfulV2 } from 'src/mentor/helpers/utilityFunctions'
import {
  BOOKING_PAYMENT_STATUSES,
  BOOKING_SESSION_STATUS,
  BOOKING_STATUSES,
  DAY_CALENDAR_EACH_SLOT_HEIGHT_PX,
  DAY_CALENDAR_EACH_SLOT_TWO_ROW_HEIGHT_PX,
  DISPATCH_EVENT_KEYS,
  EVENT_TYPE_CONSULTATION,
  EVENT_TYPE_SCREENING,
  EVENT_TYPE_THERAPY_SESSION,
  FULL_DAY_EVERYDAY_SCHEDULE,
  platformOptions,
  SIGNALING_COMMANDS,
  STATUS_SESSION_BOOKED,
  STATUS_SESSION_COMPLETED,
  VISIT_EVENT_TYPES,
} from 'src/consumer/constants'
import {
  getDefaultScheduleFromSchedules,
  getSubscriptionPlanById,
  isCommonResponseSuccessful,
  markBookedSessionAsCompletedFunction,
  sleep,
} from 'src/mentor/helpers/utilityFunctions'
import { addWindowSignalingEventListener } from 'src/live'
import {
  getAvailabilityForDayCalendar,
  getDefaultSlotDuration,
  getInitiallySelectedEventType,
  getInitiallySelectedEventTypeId,
  getMentorAvailabilityOnThisDay,
  getSlotGroupsForDayCalendar,
  getSlotDurationBasedOnCheckIn,
} from './ClinicManageBookingsHelper'
import {
  EDVCCalendarNavigator,
  EDVCNavButton,
  EDVCContainer,
  EDVCExtraSlotItemForTime,
  EDVCParent,
  EDVCPresentTimeLine,
  EDVCPresentTimeLineDot,
  EDVCPresentTimeTag,
  EDVCSlotDurationSlider,
  EDVCSlotDurationOverlay,
  EDVCSliderCloseButton,
  EDVCCollapsedHeader,
  TimeSectionHeader,
  EDVCSlotColoredItem,
  EDVCSlotItem,
  EDVCSlotItemContainer,
  EDVCSlotItemTime,
  EDVCSlotStartTime,
  HighlightedBlockContainer,
  HighlightedBlockEndMarker,
  HighlightedBlockStartMarker,
} from './ClinicManageBookingsPageStyles'

import { ConfirmCancelSessionDialog } from 'src/mentor/components/MentorEventsPage/MentorEventsComponentsAndHelpers'
import { ConfirmMarkCompletedDialog } from './components/ConfirmMarkCompletedDialog'
import './LoaderComponent.css'
import { ClinicAssistantIdlePopup } from 'src/clinic/components/CheckIn/clinicAssistantIdlePopup'
import ValidateCheckInPopup from 'src/clinic/components/CheckIn/components/ValidateCheckInPopup'
import DayViewCalendarFloatingHeader from 'src/clinic/components/ClinicManageBookingsPage/headerComponents/DayViewCalendarFloatingHeader'
import CalendarHeaderFloatingBar from 'src/clinic/components/ClinicManageBookingsPage/headerComponents/CalendarHeaderFloatingBar'
import SlotPopupSummary from 'src/clinic/components/ClinicManageBookingsPage/slotItem/SlotPopupSummary'
import {
  checkIntoCurrentlyPeekedInRoomAsInPerson,
  fetchAndProcessBookedSlots,
  fetchVitalsForBookedSlots,
  getOverlapGroupsForTimeBlock,
  getPaymentLinksForBookedEvents,
  prepareBookedSlotsWithShiftedPositions,
  shouldShowPrescriptionPdfDownloadButtons,
} from 'src/clinic/components/ClinicManageBookingsPage/utils'
import { MUIThemeConfig } from 'src/context/MUIThemeConfig'
import { useCheckedInOrPeekedInChamberRoomId } from 'src/customHooks/customHooks'
import { getAllRolesInBoolean } from 'src/mentor/layout/utils'
import { CheckedInTherapistsCard } from './CheckedInTherapistsCard'
import { NewBookingPopupWithPatientSelection } from 'src/mentor/components/MentorClinicBookingsPage/component/newBooking/NewBookingPopup'
import MyCurrentClinicRoster from 'src/mentor/components/MentorClinicBookingsPage/component/roster/MyCurrentClinicRoster'
import { MENTOR_CLINIC_BOOKING_TABS } from 'src/mentor/components/MentorClinicBookingsPage/util'
import ScheduledPatientsTable from 'src/supportPortal/views/scheduledPatients/components/ScheduledPatientsTable'
import {
  EditBookingDialogComponent,
  EditPatientDialogComponent,
  PendingActionsDCDialogComponent,
  shouldShowTwoRowDesign,
  TherapyBookingDialogComponent,
} from './ClinicManageBookingComponents'
import { VitalsComponentEntryDialog } from './VitalsComponentEntryDialog'
// Lazy load heavy components - only load when needed
const DayViewFeeComponent = lazy(() => import('./dayViewFee/DayViewFeeComponent'))
const ReceiptPrintingComponent = lazy(() => import('./receiptPrinting/ReceiptPrintingComponent'))
import { getAllClinicRelatedEventTypes } from './newBookingDialog/utils'
import {
  PastTherapyReportDialogPaper,
  ReceiptPrintDialogPaper,
} from './receiptPrinting/styledComponents'
import BookedSlotItemContent from './slotItem/BookedSlotItemContent'
import ConsultsTab from './consultsTab/ConsultsTab'
// Lazy load drawers - only load when opened
const BookedSlotsDrawer = lazy(() => import('./components/BookedSlotsDrawer'))
const ReportViewDrawer = lazy(() => import('./components/ReportViewDrawer'))
const BookedEventDetailsDrawer = lazy(() => import('./components/BookedEventDetailsDrawer'))
import { useMultipleDialogs } from './slotItem/hooks/useDialogState'
import BookedSlotDialogs from './slotItem/components/BookedSlotDialogs'
import RescheduleSlotDialog from './slotItem/components/RescheduleSlotDialog'
import { usePrescriptionDownload } from './slotItem/hooks/usePrescriptionDownload'
import { usePrescriptionDialogs } from './slotItem/hooks/usePrescriptionDialogs'
import { calculateCreatedTimeFrom, calculateCreatedTimeTo } from './slotItem/utils/timeCalculations'
import { useDispatch, useSelector } from 'react-redux'
import { selectPrescriptionViewSettingsData } from 'src/clinic/reducers/prescription/prescriptionReducer'
import { fetchPrescriptionViewSettings } from 'src/clinic/reducers/prescription/services/prescriptionViewSettingsThunk'
import { getEmrApiData } from 'src/shared/utils/calendarReportApiHelper'
import NewBookingDialogComponent from 'src/clinic/components/ClinicManageBookingsPage/newBookingDialogComponent/NewBookingDialogComponent'
import SupportBookedEventRescheduleDrawer from 'src/supportPortal/components/bookings/SupportBookedEventRescheduleDrawer'

dayjs.extend(utc)
dayjs.extend(timezone)
dayjs.extend(isBetween)
dayjs.extend(isSameOrBefore)
dayjs.extend(isSameOrAfter)
dayjs.extend(customParseFormat)

/**
 * Always move to the previous day (even when currently on today).
 * We intentionally do not guard against "today" here because users frequently
 * need to jump to yesterday for same-day follow‑ups and summaries.
 * Keeping the logic central makes it harder to accidentally reintroduce
 * the old early-return behaviour.
 */
export function goToPreviousDate(selectedDate, setSelectedDate) {
  const previousDate = dayjs(selectedDate, 'DD-MM-YYYY')
    .subtract(1, 'day')
    .format('DD-MM-YYYY')
  setSelectedDate(previousDate)
  return previousDate
}

/**
 * @typedef {Object} DayViewCalendarComponentProps
 * @property {boolean} [forMentor]
 * @property {boolean} [mentorAuthorisedToSaveAndGetPatientsUsingIdb]
 * @property {string} [mentorId]
 * @property {Object} [mentorProfileData]
 * @property {string} [currentView]
 * @property {Object} [mentorClinicAssociationData]
 * @property {Array} [allProfessionalsDataForAssistant]
 * @property {Object} [thisClinicData]
 * @property {Function} [setFetchDataTriggerForBookingRequests]
 * @property {boolean} [canPersonPrintPrescription]
 * @property {boolean} [useClinicServices]
 * @property {Array} [chambersData]
 * @property {Object} [assistantProfileInThisClinic]
 * @property {React.ReactNode} [LeftSideExtraComponent]
 * @property {React.ReactNode} [RightSideExtraComponent]
 * @property {React.Ref} [containerRefForAutoScrolling]
 * @property {Function} [bookedSlotClickFunctionOverride]
 * @property {Function} [emptySlotClickFunctionOverride]
 * @property {boolean} [minimalUI]
 * @property {boolean} [showBookingPopup]
 * @property {boolean} [useClinicAppointmentTypes]
 * @property {Array} [clinicAppointmentTypesList]
 * @property {String} [bookingListingApiSignature]
 * @property {String} [meetingBookApiSignature]
 * @property {String} [patientSearchApiSignature]
 * @property {String} [cancelAppointmentApiSignature]
 * @property {String} [editBookingApiSignature]
 * @property {String} [pastPatientBookingsApiSignature]
 * @property {String} [publicBookingJoiningLinksApiSignature]
 * @property {String} [createPatientApiSignature]
 * @property {String} [listOfPaymentLinksApiSignature]
 * @property {String} [getPrescriptionImagesApiSignature]
 * @property {String} [savePrescriptionImagesApiSignature]
 * @property {String} [fileUploadApiSignature]
 * @property {String} [sendPrescriptionPdfApiSignature]
 * @property {boolean} [hideStartButton]
 * @property {boolean} [hideMarkedCompletedButton]
 * @property {boolean} [hidePreviousTherapyReportsButton]
 * @property {boolean} [stickySideButtons]
 * @property {boolean} [compactUI]
 * @property {Object} [headerContainerStyles]
 * @property {boolean} [fireCheckInApiOnCalendarItemClick]
 * @property {boolean} [showValidateCheckInPopup]
 * @property {boolean} [hideRefreshButton]
 * @property {Array} [mentorsList]
 * @property {Function} [onChangeMentorProfileData]
 * @property {String} [sendSecureSessionLinkToPatientSignature]
 * @property {String} [sendPublicSessionLinkToPatientSignature]
 * @property {Array} [eventTypesListOverride]
 * @property {boolean} [showCancelledChipInHeader]
 * @property {boolean} [hideMentorSelection]
 * @property {boolean} [hideScrollToTopButton]
 * @property {boolean} [bookWithUnassignedMentor]
 * @property {boolean} [showGalleryForPrescriptionImages]
 * @property {boolean} [hideToggleSwitchForReportRelatedData]
 * @property {boolean} [forceHoldingSlot]
 * @property {boolean} [hideBookButton]
 * @property {boolean} [hideDropdownForMentorSelection]
 * @property {String} [paymentLinkToSendViaQRCode]
 * @property {Function} [transformClinicBrandData]
 * @property {boolean} [showPatientHistoryInUploadPrescriptionWidget]
 * @property {boolean} [forceShowJoiningDetailsActionButton]
 * @property {String} [vitalsFormGetApiSignature]
 */

/**
 * @type {React.ForwardRefExoticComponent<React.PropsWithoutRef<DayViewCalendarComponentProps> & React.RefAttributes<any>>}
 */

const DayViewCalendarComponent = forwardRef(
  /**
   * @param {DayViewCalendarComponentProps} props
   * @param {React.Ref<any>} componentRef
   */
  (
    {
      forMentor = false,
      mentorAuthorisedToSaveAndGetPatientsUsingIdb = false,
      mentorId,
      mentorProfileData,
      currentView = MENTOR_CLINIC_BOOKING_TABS.calendar.id,
      mentorClinicAssociationData,
      allProfessionalsDataForAssistant,
      thisClinicData,
      setFetchDataTriggerForBookingRequests,
      canPersonPrintPrescription,
      useClinicServices = false,
      chambersData,
      assistantProfileInThisClinic,
      LeftSideExtraComponent = <></>,
      RightSideExtraComponent = <></>,
      // Opt-in (mentor bookings page): offset the scroll-floating header past the left-menu rail and
      // drop its redundant clinic name/brand/time block. Defaults keep clinic/support portals as-is.
      floatingHeaderLeftOffset = 0,
      hideFloatingHeaderClinicDetails = false,
      // Opt-in: suppress the floating header's New booking button (the host page shows its own).
      hideFloatingHeaderNewBooking = false,
      // Opt-in: hide the floating header's date picker (the host page shows it in its main header).
      hideFloatingHeaderDatePicker = false,
      // Opt-in: hide the floating header's "more actions" (3-dots) overflow menu.
      hideFloatingHeaderOverflowMenu = false,
      // Opt-in: hide the floating header's filter pills (lifted into the host page's main header).
      hideFloatingHeaderFilterPills = false,
      // Opt-in: hide the floating header's "View all booked slots" (☰) button.
      hideFloatingHeaderBookedSlotsButton = false,
      containerRefForAutoScrolling,
      bookedSlotClickFunctionOverride,
      emptySlotClickFunctionOverride,
      minimalUI = false,
      showBookingPopup = false,
      useClinicAppointmentTypes = false,
      clinicAppointmentTypesList = [],
      bookingLisitingApiSignature,
      meetingBookApiSignature,
      patientSearchApiSignature,
      cancelAppointmentApiSignature,
      editBookingApiSignature,
      pastPatientBookingsApiSignature,
      publicBookingJoiningLinksApiSignature,
      createPatientApiSignature,
      patientUpdateApiSignature,
      patientTagsApiSignature,
      hideStartButton = false,
      hideMarkedCompletedButton = false,
      hidePreviousTherapyReportsButton = false,
      stickySideButtons = false,
      compactUI = false,
      headerContainerStyles = {},
      fireCheckInApiOnCalendarItemClick = false,
      showValidateCheckInPopup = false,
      hideRefreshButton,
      listOfPaymentLinksApiSignature,
      mentorsList,
      onChangeMentorProfileData,
      paymentLinkToSendViaQRCode,
      sendSecureSessionLinkToPatientSignature,
      sendPublicSessionLinkToPatientSignature,
      eventTypesListOverride,
      // showBInfoFilters = false,
      showCancelledChipInHeader = false,
      hideMentorSelection = false,
      hideScrollToTopButton = false,
      bookWithUnassignedMentor = false,
      getPrescriptionImagesApiSignature,
      savePrescriptionImagesApiSignature,
      fileUploadApiSignature,
      showGalleryForPrescriptionImages = false,
      hideToggleSwitchForReportRelatedData = true,
      sendPrescriptionPdfApiSignature,
      forceHoldingSlot = false,
      hideBookButton = false,
      transformClinicBrandData = (clinicBrandData) => {
        return clinicBrandData
      },
      hideDropdownForMentorSelection = false,
      showPatientHistoryInUploadPrescriptionWidget = false,
      patientHistoryGetApiSignature,
      forceShowJoiningDetailsActionButton = false,
      hideRescheduleDialog = true,
      vitalsFormGetApiSignature,
      showSendingLinkButtonsToSupport = false,
      showViewReportsButtonInActionButtons = false,
      canRescheduleBookedSlot,
      rescheduleDialogType = 'clinic-assistant',
      onOpenPatientSearch,
      onOpenPractitionerDrawer,
      practitionerButtonProps,
      hidePatientContactDetails = false,
      // External control props
      eachSlotDurationInMins: externalSlotDuration,
      setEachSlotDurationInMins: externalSetSlotDuration,
      onBookedSlotsUpdate,
      selectedDate: externalSelectedDate,
      setSelectedDate: externalSetSelectedDate,
    },
    componentRef,
  ) => {
    // configs and initial settings
    const navigate = useNavigate()
    const location = useLocation()
    const isClinicAssistantRoute = location?.pathname?.startsWith('/ca-home') || location?.pathname?.startsWith('/kiosk')
    const presentTimeLineRef = useRef(null)
    const clinicStartTimeRef = useRef({})
    const bookedEventsRef = useRef(new Map())
    const hasAutoExpandedRef = useRef(false)
    const refreshBookedSlotsRef = useRef(null)
    const prevExternalSlotDurationRef = useRef(externalSlotDuration)

    // UI Related States
    // Toggle for showing floating bar after scroll stops
    // Default to enabled (true) - user can disable if they want
    const [showCalendarHeaderAfterScroll, setShowCalendarHeaderAfterScroll] = useState(() => {
      // Load from localStorage if available, default to true if not set
      const saved = localStorage.getItem('calendar-header-sticky-enabled')
      return saved !== null ? saved === 'true' : true
    })
    
    // Track actual visibility of the floating bar (not just the toggle setting)
    const [isFloatingBarActuallyVisible, setIsFloatingBarActuallyVisible] = useState(false)
    
    // Check initial visibility on mount
    useEffect(() => {
      if (showCalendarHeaderAfterScroll && typeof document !== 'undefined') {
        const calendarHeader = document.querySelector('[data-calendar-header="true"]')
        if (calendarHeader) {
          const rect = calendarHeader.getBoundingClientRect()
          const isOutOfView = rect.bottom < 0
          if (isOutOfView) {
            setIsFloatingBarActuallyVisible(true)
          }
        }
      }
    }, [showCalendarHeaderAfterScroll])
    
    // Listen for floating bar visibility changes
    useEffect(() => {
      const handleFloatingBarVisibility = (event) => {
        setIsFloatingBarActuallyVisible(event.detail?.show || false)
      }
      
      window.addEventListener('show-calendar-header-after-scroll', handleFloatingBarVisibility)
      
      return () => {
        window.removeEventListener('show-calendar-header-after-scroll', handleFloatingBarVisibility)
      }
    }, [])
    
    // Save to localStorage when changed
    useEffect(() => {
      localStorage.setItem('calendar-header-sticky-enabled', showCalendarHeaderAfterScroll.toString())
    }, [showCalendarHeaderAfterScroll])
    
    // Calculate initial slot height based on default slot duration
    // REQUIREMENT: Double the height of each slot in calendar
    const initialSlotDuration = externalSlotDuration || getDefaultSlotDuration(mentorProfileData)
    const BASE_HEIGHT_PER_MINUTE = 8 // Doubled from 4
    const MIN_HEIGHT = 96 // Doubled from 48
    const MAX_HEIGHT = 240 // Doubled from 120
    const initialHeight = Math.max(
      MIN_HEIGHT,
      Math.min(MAX_HEIGHT, Math.max(initialSlotDuration || 15, 15) * BASE_HEIGHT_PER_MINUTE)
    )
    const [heightOfEachSlot, setHeightOfEachSlot] = useState(initialHeight)
    const [eachSlotDurationInMins, setEachSlotDurationInMins] = useState(
      Math.max(initialSlotDuration || 15, 15),
    )
    // Ref to track if user has manually set the slot duration (to prevent automatic resets)
    const userManuallySetSlotDurationRef = useRef(false)
    // State to track manual setting for UI updates
    const [isSlotDurationManuallySet, setIsSlotDurationManuallySet] = useState(false)

    // Sync with external slot duration if provided
    useEffect(() => {
      if (externalSlotDuration !== undefined && externalSlotDuration !== prevExternalSlotDurationRef.current) {
        console.log('Updating slot duration from', prevExternalSlotDurationRef.current, 'to', externalSlotDuration)
        prevExternalSlotDurationRef.current = externalSlotDuration
        setEachSlotDurationInMins(externalSlotDuration)
        const newHeight = Math.max(
          MIN_HEIGHT,
          Math.min(MAX_HEIGHT, externalSlotDuration * BASE_HEIGHT_PER_MINUTE)
        )
        setHeightOfEachSlot(newHeight)
        userManuallySetSlotDurationRef.current = true
        setIsSlotDurationManuallySet(true)
      }
    }, [externalSlotDuration, MIN_HEIGHT, MAX_HEIGHT, BASE_HEIGHT_PER_MINUTE])

    const [activeSliderSlotId, setActiveSliderSlotId] = useState(null)
    const [activeSlotElement, setActiveSlotElement] = useState(null)
    const sliderRef = useRef(null)
    const sliderSlotRef = useRef(null)
    const animationFrameRef = useRef(null)
    
    // Handle overlay and slot highlighting when slider is active
    useEffect(() => {
      if (activeSliderSlotId && sliderSlotRef.current) {
        // Find the slot element from bookedEventsRef
        const slotElement = bookedEventsRef.current.get(sliderSlotRef.current)
        if (slotElement) {
          setActiveSlotElement(slotElement)
          // Add highlight class and ensure it's visible
          slotElement.classList.add('highlighted-slot')
          // Force z-index and visibility to ensure it's above overlay
          slotElement.style.zIndex = '1502'
          slotElement.style.position = 'relative'
          slotElement.style.opacity = '1'
          // Scroll to center the slot only once when opening
          setTimeout(() => {
            slotElement.scrollIntoView({ behavior: 'smooth', block: 'center' })
          }, 100)
        }
      } else {
        // Remove highlight when slider closes
        if (activeSlotElement) {
          activeSlotElement.classList.remove('highlighted-slot')
          activeSlotElement.style.zIndex = ''
          activeSlotElement.style.position = ''
          activeSlotElement.style.opacity = ''
          setActiveSlotElement(null)
        }
      }
      
      return () => {
        if (activeSlotElement) {
          activeSlotElement.classList.remove('highlighted-slot')
          activeSlotElement.style.zIndex = ''
          activeSlotElement.style.position = ''
          activeSlotElement.style.opacity = ''
        }
      }
    }, [activeSliderSlotId, activeSlotElement])
    
    // Cleanup animation frame on unmount
    useEffect(() => {
      return () => {
        if (animationFrameRef.current) {
          cancelAnimationFrame(animationFrameRef.current)
        }
      }
    }, [])

    // Selections, Data and loading related states
    const [selectedDate, setSelectedDate] = useState(dayjs().format('DD-MM-YYYY'))
    
    // Listen for external date changes from header
    useEffect(() => {
      const handleDateChange = (event) => {
        if (event.detail?.date && event.detail.date !== selectedDate) {
          setSelectedDate(event.detail.date)
        }
      }
      window.addEventListener('calendar-date-change', handleDateChange)
      return () => {
        window.removeEventListener('calendar-date-change', handleDateChange)
      }
    }, [selectedDate])
    
    // Dispatch date changes to header
    useEffect(() => {
      window.dispatchEvent(new CustomEvent('calendar-internal-date-change', { detail: { date: selectedDate } }))
    }, [selectedDate])
    const timeZone = encodeURIComponent(getTimezoneOffset())
    const [selectedEventTypeId, setSelectedEventTypeId] = useState(
      getInitiallySelectedEventTypeId(mentorProfileData?.eventTypes, thisClinicData?.id),
    )
    const [selectedVisitEventType, setSelectedVisitEventType] = useState()

    const [eventTypesVisible, setEventTypesVisible] = useState([])

    // Start with loading state true to show skeleton on initial render
    const [bookedSlotsInDayLoading, setBookedSlotsInDayLoading] = useState(true)
    const [planDataForSelectedEventId, setPlanDataForSelectedEventId] = useState()
    const [showCancelledOnly, setShowCancelledOnly] = useState(false)
    const [cancelledCount, setCancelledCount] = useState(0)
    const [cancelDialogData, setCancelDialogData] = useState(null)
    const [markCompletedDialogData, setMarkCompletedDialogData] = useState(null)
    
    // Collapse state for early morning and late night sections
    const [collapsedRanges, setCollapsedRanges] = useState({
      earlyMorning: true, // 12 AM - 7 AM
      lateNight: true,    // After 10 PM
    })
    const [selectedClinicBrandData, setSelectedClinicBrandData] = useState()
    const [bookedSlotsDrawerOpen, setBookedSlotsDrawerOpen] = useState(false)
    const [reportViewDrawerOpen, setReportViewDrawerOpen] = useState(false)
    const [selectedBookedSlotForReport, setSelectedBookedSlotForReport] = useState(null)
    const [selectedFilterType, setSelectedFilterType] = useState('all') // 'all', 'inPerson', 'video'
    const [clinicAssociations, setClinicAssociations] = useState([])

    const [paymentLinks, setPaymentLinks] = useState()
    const [bookedSlotsPublicLinksInfo, setBookedSlotsPublicLinksInfo] = useState([])
    const [bookedSlots, setBookedSlotsInDay] = useState([])
    const [includeReportRelatedData, setIncludeReportRelatedData] = useState(false)
    const prescrpitionViewSettingsStoreData = useSelector(selectPrescriptionViewSettingsData)
    const dispatch = useDispatch()

    useEffect(() => {
      if (typeof onBookedSlotsUpdate === 'function') {
        onBookedSlotsUpdate(bookedSlots)
      }
    }, [bookedSlots, onBookedSlotsUpdate])

    const bookedSlotsInDay = useMemo(() => {
      const relevantBookedSlots = bookedSlots?.filter((slot) => {
        if (showCancelledOnly) {
          return slot?.status === BOOKING_SESSION_STATUS.CANCELLED
        }
        return slot?.status !== BOOKING_SESSION_STATUS.CANCELLED
      })

      let bookedSlotsToReturn = relevantBookedSlots?.map((bookedSlot) => {
        const slotPaymentLinks = paymentLinks?.filter(
          (link) => link?.bookedEventId === bookedSlot?.id,
        )
        const publicJoiningLinks = (bookedSlotsPublicLinksInfo || [])?.filter(
          (publicSessionInfo) => publicSessionInfo?.bookedEventId === bookedSlot?.id,
        )
        return {
          ...bookedSlot,
          paymentLinks: slotPaymentLinks,
          publicJoiningLinks: publicJoiningLinks,
        }
      })
      bookedSlotsToReturn = prepareBookedSlotsWithShiftedPositions(bookedSlotsToReturn, timeZone)
      return bookedSlotsToReturn
    }, [bookedSlots, paymentLinks, bookedSlotsPublicLinksInfo, showCancelledOnly, timeZone])

    // Reusable function to calculate optimal slot height
    const calculateOptimalSlotHeight = useCallback((bookedSlotsToConsider = null) => {
      // REQUIREMENT: Double the height of each slot in calendar
      // Base height per minute (doubled from 4px to 8px per minute)
      const BASE_HEIGHT_PER_MINUTE = 8
      // Minimum height for usability - doubled from 60/48 to 120/96
      const isMobile = typeof window !== 'undefined' && window.innerWidth < 768
      const MIN_HEIGHT = isMobile ? 120 : 96 // Doubled from 60px/48px
      // Maximum height to prevent slots from being too tall (doubled from 120px to 240px)
      const MAX_HEIGHT = 240
      
      // Use provided booked slots or current state
      const slotsToCheck = bookedSlotsToConsider !== null ? bookedSlotsToConsider : bookedSlotsInDay
      
      // If there are booked slots, consider their durations
      if (slotsToCheck && slotsToCheck.length > 0) {
        // Find the minimum duration among booked slots
        let minBookedDuration = Infinity
        slotsToCheck.forEach((slot) => {
          const startTime = slot?.startTimeForDayCal || slot?.startTime
          const endTime = slot?.endTimeForDayCal || slot?.endTime
          if (startTime && endTime) {
            const duration = dayjs(endTime).diff(dayjs(startTime), 'minutes')
            if (duration > 0 && duration < minBookedDuration) {
              minBookedDuration = duration
            }
          }
        })
        
        // Use the smaller of: slot duration or minimum booked slot duration
        const effectiveDuration = minBookedDuration !== Infinity 
          ? Math.min(eachSlotDurationInMins, minBookedDuration)
          : eachSlotDurationInMins
        
        // Calculate height based on effective duration
        let calculatedHeight = effectiveDuration * BASE_HEIGHT_PER_MINUTE
        
        // Apply min/max constraints
        calculatedHeight = Math.max(MIN_HEIGHT, Math.min(MAX_HEIGHT, calculatedHeight))
        
        // If two-row design is needed, add extra height (doubled from 70/64 to 140/128)
        if (shouldShowTwoRowDesign(slotsToCheck)) {
          calculatedHeight = Math.max(calculatedHeight, isMobile ? 140 : 128) // Doubled from 70px/64px
        }
        
        return calculatedHeight
      } else {
        // No booked slots: use slot duration with min/max constraints
        let calculatedHeight = eachSlotDurationInMins * BASE_HEIGHT_PER_MINUTE
        calculatedHeight = Math.max(MIN_HEIGHT, Math.min(MAX_HEIGHT, calculatedHeight))
        return calculatedHeight
      }
    }, [bookedSlotsInDay, eachSlotDurationInMins])

    // Filter-pill bridge (mentor flow): mirror the filter context to a lifted strip in the main
    // page header, and accept its toggles back — same lightweight event bridge the date nav uses.
    useEffect(() => {
      if (!forMentor) return undefined
      const broadcast = () => {
        window.dispatchEvent(
          new CustomEvent('calendar-filter-context', {
            detail: {
              bookedSlotsInDay,
              selectedFilterType,
              cancelledCount,
              showCancelledOnly,
              showCancelledChip: showCancelledChipInHeader,
              includeReportRelatedData,
              hideToggleSwitchForReportRelatedData,
              eachSlotDurationInMins,
              slotDurationApplicable: currentView === MENTOR_CLINIC_BOOKING_TABS.calendar.id,
            },
          }),
        )
      }
      broadcast()
      window.addEventListener('calendar-request-filter-context', broadcast)
      return () => window.removeEventListener('calendar-request-filter-context', broadcast)
    }, [
      forMentor,
      bookedSlotsInDay,
      selectedFilterType,
      cancelledCount,
      showCancelledOnly,
      showCancelledChipInHeader,
      includeReportRelatedData,
      hideToggleSwitchForReportRelatedData,
      eachSlotDurationInMins,
      currentView,
    ])

    useEffect(() => {
      if (!forMentor) return undefined
      const onSetFilter = (e) => e?.detail && setSelectedFilterType(e.detail.value)
      const onSetCancelled = (e) => e?.detail && setShowCancelledOnly(e.detail.value)
      const onSetReports = (e) => e?.detail && setIncludeReportRelatedData(e.detail.value)
      const onRefresh = () => refreshBookedSlotsRef.current?.()
      const onSetSlotDuration = (e) => {
        if (!e?.detail) return
        userManuallySetSlotDurationRef.current = true
        setIsSlotDurationManuallySet(true)
        setEachSlotDurationInMins(e.detail.value)
      }
      window.addEventListener('calendar-set-filter', onSetFilter)
      window.addEventListener('calendar-set-cancelled', onSetCancelled)
      window.addEventListener('calendar-set-reports', onSetReports)
      window.addEventListener('calendar-refresh', onRefresh)
      window.addEventListener('calendar-set-slot-duration', onSetSlotDuration)
      return () => {
        window.removeEventListener('calendar-set-filter', onSetFilter)
        window.removeEventListener('calendar-set-cancelled', onSetCancelled)
        window.removeEventListener('calendar-set-reports', onSetReports)
        window.removeEventListener('calendar-refresh', onRefresh)
        window.removeEventListener('calendar-set-slot-duration', onSetSlotDuration)
      }
    }, [forMentor])

    const roomId = useCheckedInOrPeekedInChamberRoomId()

    // Assistant specific data:
    const [pendingActionsOfPerson, setPendingActionsOfPerson] = useState()

    // Constants and direct use values:
    const selectedClinic = thisClinicData
    const selectedDateStartTime = dayjs(selectedDate, 'DD-MM-YYYY')?.format(
      `YYYY-MM-DDTHH:mm:ss[${decodeURIComponent(timeZone)}]`,
    )
    const selectedDateEndTime = dayjs(selectedDate, 'DD-MM-YYYY')
      ?.endOf('day')
      ?.format(`YYYY-MM-DDTHH:mm:ss[${decodeURIComponent(timeZone)}]`)

    const selectedDateUtcStartTime = dayjs
      .utc(selectedDateStartTime)
      .format(`YYYY-MM-DDTHH:mm:ss[Z]`)
    const selectedDateUtcEndTime = dayjs
      .utc(selectedDateEndTime)
      .format(`YYYY-MM-DDTHH:mm:ss[Z]`)
    const eventDataForSelectedEventType = useMemo(() => {
      return mentorProfileData?.eventTypes?.find(
        (eventType) => eventType?.uid === selectedEventTypeId,
      )
    }, [selectedEventTypeId, mentorProfileData])

    const availabilityOfDay = useMemo(() => {
      return getAvailabilityForDayCalendar(selectedDate, timeZone, {
        timeZone: timeZone,
        ...FULL_DAY_EVERYDAY_SCHEDULE, // decided on 24 hour calendar for all events so -
        // ...(eventDataForSelectedEventType?.type === allAvailableEventTypes.screening ? FULL_DAY_EVERYDAY_SCHEDULE : scheduleForSelectedEventType)
      })
    }, [selectedDate, timeZone])
    const scheduleForSelectedEventType = useMemo(() => {
      if (
        eventDataForSelectedEventType?.contextId === selectedClinic?.id &&
        Array.isArray(mentorClinicAssociationData) &&
        mentorClinicAssociationData?.length > 0
      ) {
        if (
          mentorProfileData?.schedules?.some((schedule) =>
            schedule?.scopeSchedules?.includes(selectedClinic?.id),
          )
        ) {
          const arrayOfAllSchedulesAssoicatedWithThisClinic = mentorProfileData?.schedules?.filter(
            (schedule) => schedule?.scopeSchedules?.includes(selectedClinic?.id),
          )
          const clinicAssociatedScheduleWithThisEventId =
            arrayOfAllSchedulesAssoicatedWithThisClinic?.find((schedule) =>
              schedule?.eventIds?.includes(selectedClinic?.id),
            )
          if (clinicAssociatedScheduleWithThisEventId) {
            return clinicAssociatedScheduleWithThisEventId
          } else if (arrayOfAllSchedulesAssoicatedWithThisClinic?.length > 0) {
            return arrayOfAllSchedulesAssoicatedWithThisClinic?.[0]
          }
        } else {
          const defaultClinicSchedule = getDefaultScheduleFromSchedules(selectedClinic?.schedules)
          if (defaultClinicSchedule) {
            return defaultClinicSchedule
          }
        }
      } else {
        const scheduleWithRequestedEventId = mentorProfileData?.schedules?.find((schedule) =>
          schedule?.eventIds?.includes(selectedEventTypeId),
        )
        if (scheduleWithRequestedEventId) {
          return scheduleWithRequestedEventId
        }
      }
    }, [
      eventDataForSelectedEventType?.contextId,
      selectedClinic?.id,
      selectedClinic?.schedules,
      mentorClinicAssociationData,
      mentorProfileData?.schedules,
      selectedEventTypeId,
    ])
    const highlightedForAvailabilitySlotGroups = useMemo(() => {
      return getMentorAvailabilityOnThisDay(selectedDate, timeZone, scheduleForSelectedEventType)
    }, [selectedDate, timeZone, scheduleForSelectedEventType])
    const slotGroups = getSlotGroupsForDayCalendar(
      availabilityOfDay,
      eachSlotDurationInMins,
      timeZone,
      bookedSlotsInDay,
    )

    // Split slotGroups into three sections: Early Morning, Work Day, Late Night
    const splitSlotsIntoTimeSections = useMemo(() => {
      if (!slotGroups || slotGroups.length === 0) return { earlyMorning: [], workDay: [], lateNight: [] }
      
      const sections = {
        earlyMorning: [], // 12 AM - 7 AM
        workDay: [],      // 7 AM - 10 PM
        lateNight: [],   // 10 PM - 11:59 PM
      }
      
      slotGroups.forEach((timeBlock) => {
        if (!timeBlock || timeBlock.length === 0) return
        
        const earlyMorningSlots = []
        const workDaySlots = []
        const lateNightSlots = []
        
        timeBlock.forEach((slot) => {
          const slotTime = dayjs(slot?.startTime)
          if (!slotTime.isValid()) return
          
          const hour = slotTime.hour()
          
          // Early Morning: 12 AM (0) to 7 AM
          if (hour >= 0 && hour < 7) {
            earlyMorningSlots.push(slot)
          }
          // Work Day: 7 AM to 10 PM
          else if (hour >= 7 && hour < 22) {
            workDaySlots.push(slot)
          }
          // Late Night: 10 PM (22) onwards
          else if (hour >= 22) {
            lateNightSlots.push(slot)
          }
        })
        
        if (earlyMorningSlots.length > 0) {
          sections.earlyMorning.push(earlyMorningSlots)
        }
        if (workDaySlots.length > 0) {
          sections.workDay.push(workDaySlots)
        }
        if (lateNightSlots.length > 0) {
          sections.lateNight.push(lateNightSlots)
        }
      })
      
      return sections
    }, [slotGroups])

    // Popups, Anchors opening states and their data
    const [clickedBookedSlotAnchor, setClickedBookedSlotAnchor] = useState(null)
    const [clickedBookedSlotData, setClickedBookedSlotData] = useState({})
    
    // Drawer state for support portal
    const [bookedEventDetailsDrawerOpen, setBookedEventDetailsDrawerOpen] = useState(false)
    const [selectedBookedSlotForDrawer, setSelectedBookedSlotForDrawer] = useState(null)
    // Track calendar header height for spacer when using sticky positioning
    const [calendarHeaderHeight, setCalendarHeaderHeight] = useState(0)
    
    // Listen to calendar header height changes to add proper spacing
    useEffect(() => {
      const handleHeaderHeightChange = (event) => {
        const height = event.detail?.height || 0
        if (height > 0 && height !== calendarHeaderHeight) {
          setCalendarHeaderHeight(height)
        }
      }
      
      window.addEventListener('calendar-header-height-change', handleHeaderHeightChange)
      
      // Also check header height on mount
      const calendarHeader = document.querySelector('[data-calendar-header="true"]')
      if (calendarHeader) {
        const height = calendarHeader.offsetHeight || calendarHeader.getBoundingClientRect().height
        if (height > 0) {
          setCalendarHeaderHeight(height)
        }
      }
      
      return () => {
        window.removeEventListener('calendar-header-height-change', handleHeaderHeightChange)
      }
    }, [calendarHeaderHeight])
    
    // Dialog states for drawer actions (using the same hook as BookedSlotItemContent)
    const drawerActionDialogs = useMultipleDialogs([
      'sendTherapy',
      'paymentLinks',
      'joiningDetails',
      'bookedEventLogs',
      'uploadPrescriptionImages',
      'sendAssessment',
      'patientBooking',
      'emrImages',
      'reschedule',
    ])
    
    // Prescription download for drawer
    const drawerPrescriptionDownload = usePrescriptionDownload({
      bookedSlot: selectedBookedSlotForDrawer,
      sendPrescriptionPdfApiSignature,
      refreshBookedSlots: () => refreshBookedSlotsRef.current?.(),
    })
    
    const drawerPrescriptionDialogs = usePrescriptionDialogs(drawerPrescriptionDownload)
    
    // Check if we're in support portal
    const isSupportPortal = typeof window !== 'undefined' && window.location?.pathname?.includes('/support')

    const [clickedWantedSlotData, setClickedWantedSlotData] = useState({})

    const [newBookingDialogOpen, setNewBookingDialogOpen] = useState(false)
    const [preSelectedPatientForBooking, setPreSelectedPatientForBooking] = useState(null)
    const [editBookingDialogOpen, setEditBookingDialogOpen] = useState(false)
    const [standaloneRescheduleSlot, setStandaloneRescheduleSlot] = useState(null)
    const [standaloneRescheduleOpen, setStandaloneRescheduleOpen] = useState(false)

    const [editPatientDialogOpen, setEditPatientDialogOpen] = useState(false)
    const [pendingActionsDialogOpen, setPendingActionsDialogOpen] = useState(false)

    const [addVitalsDialogOpen, setAddVitalsDialogOpen] = useState(false)

    const [therapyBookingDialogOpen, setTherapyBookingDialogOpen] = useState(false)
    const [dataForTherapyBookingDialog, setDataForTherapyBookingDialog] = useState(false)

    const [dataForReceiptPrinting, setDataForReceiptPrinting] = useState({
      eventId: null,
      eventData: null,
    })
    const [openReceiptPrintingDialog, setOpenReceiptPrintingDialog] = useState(false)
    const [importedFromItems, setImportedFromItems] = useState([])
    const isRescheduleVisibleForSlot = useCallback(
      (slot) => {
        const isBooked =
          slot?.status === STATUS_SESSION_BOOKED || slot?.status === BOOKING_STATUSES.BOOKED
        const isBlockedForPayment =
          slot?.status === BOOKING_STATUSES.BOOKING_INITIATED &&
          slot?.paymentStatus === BOOKING_PAYMENT_STATUSES.PENDING
        return Boolean(
          slot?.id &&
            isBooked &&
            !isBlockedForPayment &&
            !slot?.markedCompleted &&
            !hideRescheduleDialog &&
            (!canRescheduleBookedSlot || canRescheduleBookedSlot(slot)),
        )
      },
      [canRescheduleBookedSlot, hideRescheduleDialog],
    )
    const closeStandaloneReschedule = useCallback(() => {
      setStandaloneRescheduleOpen(false)
      setStandaloneRescheduleSlot(null)
    }, [])
    const openStandaloneReschedule = useCallback((slot) => {
      if (!slot) return
      setStandaloneRescheduleSlot(slot)
      setStandaloneRescheduleOpen(true)
    }, [])

    // const bookedSlotsInDay = useMemo(() => {
    //   return bookedSlots?.map((bookedSlot) => {
    //     const slotPaymentLinks = pay
    //     return {
    //       ...bookedSlot,
    //       bookedSlotData: bookedSlotData,
    //     }
    //   })
    // }, [bookedSlots, paymentLinks])

    const isViewingPlannedTab = currentView === MENTOR_CLINIC_BOOKING_TABS.planned.id
    const isViewingCalendarTab = currentView === MENTOR_CLINIC_BOOKING_TABS.calendar.id
    const isViewingConsultsTab = currentView === MENTOR_CLINIC_BOOKING_TABS.appointments.id
    const isViewingRosterTab = currentView === MENTOR_CLINIC_BOOKING_TABS.roster.id
    const isViewingCalendarRelatedTab = isViewingCalendarTab || isViewingConsultsTab

    async function getPaymentLinks(bookedEventIds) {
      const data = await getPaymentLinksForBookedEvents(
        bookedEventIds,
        listOfPaymentLinksApiSignature,
      )
      setPaymentLinks(data)
      return data
    }

    async function getPublicBookingJoiningLinks(bookedEvents) {
      const bookedEventIds = bookedEvents?.map((bookedEvent) => bookedEvent?.id)
      if (bookedEventIds?.length < 0) {
        return []
      }
      if (!getPublicBookingJoiningLinks) {
        return []
      }
      // const resp = getCPDataWithParams(`/api/secure/cc/support/events/video-session-tokens/get`, {
      //   bookedEventIds: bookedEventIds?.join(','),
      // })
      const resp = await getCPDataWithParams(publicBookingJoiningLinksApiSignature, {
        'booked-event-ids': bookedEventIds?.join(','),
      })
      if (resp?.status === 200) {
        setBookedSlotsPublicLinksInfo(resp?.data)
      }
    }

    // API calls and effects:

    // distinction between clinic assistant api and mentor api
    const getAndSetBookedSlotsOfSelectedDate = useCallback(async () => {
      if (mentorId) {
        setBookedSlotsInDayLoading(true)
        const eventDataResponse = await fetchAndProcessBookedSlots({
          apiSignature: bookingLisitingApiSignature,
          startTime: selectedDateUtcStartTime,
          endTime: selectedDateUtcEndTime,
          timeZone,
          forMentor,
          includeReportRelatedData,
        })

        console.log({ eventDataResponse })

        if (eventDataResponse !== null) {
          let bookedSlots = eventDataResponse?.bookedSlotWithPatientInfo || []
          
          // Filter by clinic if viewing a specific clinic location (for mentor view)
          // Only filter if we have a specific clinic selected (thisClinicData is set)
          // BUT: In support portal, show ALL slots from all clinics (don't filter)
          if (forMentor && !isSupportPortal && thisClinicData?.id && thisClinicData?.clinicBrandId && bookedSlots.length > 0) {
            const originalCount = bookedSlots.length
            const clinicIdToMatch = String(thisClinicData.id)
            const clinicBrandIdToMatch = String(thisClinicData.clinicBrandId)
            
            bookedSlots = bookedSlots.filter((slot) => {
              // Normalize IDs to strings for comparison
              const slotClinicId = slot?.clinicId ? String(slot.clinicId) : null
              const slotClinicLocationId = slot?.clinicLocationId ? String(slot.clinicLocationId) : null
              const slotClinicBrandId = slot?.clinicBrandId ? String(slot.clinicBrandId) : null
              
              // Match by clinic location ID (can be clinicId or clinicLocationId field)
              const matchesClinicId = 
                slotClinicId === clinicIdToMatch ||
                slotClinicLocationId === clinicIdToMatch
              
              // Match by clinic brand ID
              const matchesClinicBrandId = slotClinicBrandId === clinicBrandIdToMatch
              
              return matchesClinicId && matchesClinicBrandId
            })
            
            // Debug log to help identify filtering issues
            if (bookedSlots.length === 0 && originalCount > 0) {
              console.warn('All bookings filtered out. Selected clinic:', {
                id: thisClinicData?.id,
                clinicBrandId: thisClinicData?.clinicBrandId,
                title: thisClinicData?.title,
              }, 'Total slots before filter:', originalCount, 'Sample slot clinicIds:', {
                clinicId: eventDataResponse?.bookedSlotWithPatientInfo?.[0]?.clinicId,
                clinicLocationId: eventDataResponse?.bookedSlotWithPatientInfo?.[0]?.clinicLocationId,
                clinicBrandId: eventDataResponse?.bookedSlotWithPatientInfo?.[0]?.clinicBrandId,
              })
            }
          }
          
          const cancelledCount = bookedSlots?.filter(
            (item) => item?.status === BOOKING_SESSION_STATUS.CANCELLED,
          )?.length || 0
          setCancelledCount(cancelledCount)
          
          // Calculate optimal slot height based on slot duration and booked slots
          // But only if user hasn't manually set the slot duration
          if (!userManuallySetSlotDurationRef.current) {
            const optimalHeight = calculateOptimalSlotHeight(bookedSlots)
            setHeightOfEachSlot(optimalHeight)
            setIsSlotDurationManuallySet(false)
          }
          
          setBookedSlotsInDay(bookedSlots)
          getPaymentLinks(
            bookedSlots
              ?.filter(
                (item) =>
                  item?.status === BOOKING_STATUSES.BOOKING_INITIATED &&
                  item?.paymentStatus === BOOKING_PAYMENT_STATUSES.PENDING,
              )
              ?.map((item) => item?.id),
          )
          getPublicBookingJoiningLinks(bookedSlots)
          
          // Fetch vitals for booked slots asynchronously (lazy loading)
          // This runs in the background and updates slots when vitals are available
          // Always fetch vitals when we have slots (independent of includeReportRelatedData)
          // Vitals are basic information that should be available regardless of report data toggle
          if (bookedSlots.length > 0) {
            // Fetch vitals asynchronously - don't block the UI
            fetchVitalsForBookedSlots(bookedSlots)
              .then((slotsWithVitals) => {
                // Update state with vitals data
                setBookedSlotsInDay((prevSlots) => {
                  // Merge vitals data into existing slots by matching IDs
                  const updatedSlots = prevSlots.map((prevSlot) => {
                    const updatedSlot = slotsWithVitals.find((slot) => slot.id === prevSlot.id)
                    if (updatedSlot && (updatedSlot.vital || updatedSlot.vitals)) {
                      return updatedSlot
                    }
                    return prevSlot
                  })
                  return updatedSlots
                })
              })
              .catch((error) => {
                // Silently handle errors - vitals are optional and shouldn't block the UI
                console.error('Error fetching vitals for booked slots:', error)
              })
          }
          if (!forMentor) {
            const responsePendingActions = await getCPDataWithoutParams(
              `/api/secure/patient/patient-actions/pending-on-me?clinicId=${selectedClinic?.id}`,
            )
            setPendingActionsOfPerson(responsePendingActions?.data?.data?.pendingActions)
          }
        }
        setBookedSlotsInDayLoading(false)

        let finalSignatureToUse = bookingLisitingApiSignature
        if (!finalSignatureToUse?.includes('?')) {
          finalSignatureToUse = finalSignatureToUse + '?'
        }
        if (finalSignatureToUse.endsWith('?')) {
          finalSignatureToUse += `startTime=${selectedDateUtcStartTime}&endTime=${selectedDateUtcEndTime}`
        } else {
          finalSignatureToUse += `&startTime=${selectedDateUtcStartTime}&endTime=${selectedDateUtcEndTime}`
        }

        // let response
        // response = await getCPDataWithoutParams(finalSignatureToUse)
        // // if (forMentor) {
        // //   response = await getCPDataWithoutParams(
        // //     `/api/secure/teacher/booked-slots/all-with-patient-info?&get-patient-details=true`,
        // //   )
        // // } else {
        // //   response = await getCPDataWithoutParams(
        // //     `/api/secure/clinic-assistant/booked-slots/all?mentorId=${mentorId}&doctorId=${mentorId}&startTime=${selectedDateUtcStartTime}&endTime=${selectedDateUtcEndTime}`,
        // //   )
        // // }
        // if (!forMentor) {
        //   const responsePendingActions = await getCPDataWithoutParams(
        //     `/api/secure/patient/patient-actions/pending-on-me?clinicId=${selectedClinic?.id}`,
        //   )
        //   setPendingActionsOfPerson(responsePendingActions?.data?.data?.pendingActions)
        // }
        // setBookedSlotsInDayLoading(false)

        // let bookedSlots = response?.data
        // let patients = []

        // if (response?.status === 200) {
        //   if (forMentor) {
        //     bookedSlots = response?.data?.data?.bookedEvents || []
        //     patients = response?.data?.data?.patientInfo || []
        //   }

        //   const bookedSlotsPerUtc = bookedSlots?.sort(customSortForDayCalendar)
        //   let bookedSlotsPeruser = bookedSlotsPerUtc
        //   if (shouldShowTwoRowDesign(bookedSlotsPeruser)) {
        //     setHeightOfEachSlot(DAY_CALENDAR_EACH_SLOT_TWO_ROW_HEIGHT_PX)
        //   } else {
        //     setHeightOfEachSlot(DAY_CALENDAR_EACH_SLOT_HEIGHT_PX)
        //   }

        //   const cancelledToday = (bookedSlotsPeruser || [])?.filter(
        //     (item) => item?.status === STATUS_SESSION_CANCELLED,
        //   )
        //   setCancelledCount(cancelledToday?.length)

        //   getPaymentLinks(
        //     bookedSlotsPeruser
        //       ?.filter(
        //         (item) =>
        //           item?.status === BOOKING_STATUSES.BOOKING_INITIATED &&
        //           item?.paymentStatus === BOOKING_PAYMENT_STATUSES.PENDING,
        //       )
        //       ?.map((item) => item?.id),
        //   )

        //   getPublicBookingJoiningLinks(bookedSlotsPeruser)
        //   setBookedSlotsInDay(
        //     bookedSlotsPeruser?.map((bInfo) => {
        //       const patInfo = patients?.find((pInfo) => pInfo?.id === bInfo?.patientId)
        //       return { ...bInfo, patientInfo: patInfo || null }
        //     }),
        //   )
        // }
      }
    }, [mentorId, bookingLisitingApiSignature, selectedDateUtcStartTime, selectedDateUtcEndTime, timeZone, forMentor, includeReportRelatedData, onBookedSlotsUpdate, thisClinicData?.id, thisClinicData?.clinicBrandId])

    // Store the function in ref for use by other components
    useEffect(() => {
      refreshBookedSlotsRef.current = getAndSetBookedSlotsOfSelectedDate
    }, [getAndSetBookedSlotsOfSelectedDate])

    // Function to reset slot duration to system-determined value
    const resetSlotDurationToSystem = useCallback(() => {
      userManuallySetSlotDurationRef.current = false
      setIsSlotDurationManuallySet(false)
      // Trigger recalculation by re-running the useEffect logic
      let checkInEventOptionId = null
      let checkInEventOptions = null
      let roomDataEventTypes = null
      
      try {
        const checkedInDetails = localStorage.getItem('checkedInDetails')
        if (checkedInDetails) {
          const parsed = JSON.parse(checkedInDetails)
          checkInEventOptionId = parsed?.selectedCheckInEventOption
        }
      } catch (error) {
        console.error('Error reading check-in details:', error)
      }

      if (roomId && chambersData && Array.isArray(chambersData)) {
        const currentRoom = chambersData.find((room) => room?.id === roomId)
        if (currentRoom?.checkInEventOptions) {
          checkInEventOptions = currentRoom.checkInEventOptions
        }
      }

      // Get roomDataEventTypes from localStorage
      try {
        const storedRoomDataEventTypes = localStorage.getItem('roomDataEventTypes')
        if (storedRoomDataEventTypes) {
          roomDataEventTypes = JSON.parse(storedRoomDataEventTypes)
        }
      } catch (error) {
        console.error('Error reading roomDataEventTypes:', error)
      }

      const slotDuration = getSlotDurationBasedOnCheckIn(
        mentorProfileData,
        selectedClinic?.id,
        checkInEventOptionId,
        checkInEventOptions,
        roomDataEventTypes,
      )
      console.log('[resetSlotDurationToSystem] Calculated slot duration:', slotDuration)
      // Allow minimum of 5 mins (not 15) to support shorter event types like screening
      const newSlotDuration = Math.max(slotDuration || 15, 5)
      console.log('[resetSlotDurationToSystem] Setting slot duration to:', newSlotDuration)
      
      // Update prevExternalSlotDurationRef to prevent external duration from overriding
      prevExternalSlotDurationRef.current = newSlotDuration
      
      // Set the slot duration
      setEachSlotDurationInMins(newSlotDuration)
      
      // Also update the slot height to match the new duration
      const newHeight = Math.max(
        MIN_HEIGHT,
        Math.min(MAX_HEIGHT, newSlotDuration * BASE_HEIGHT_PER_MINUTE)
      )
      setHeightOfEachSlot(newHeight)
      
      // Ensure the ref is set to false so future automatic updates work
      userManuallySetSlotDurationRef.current = false
      setIsSlotDurationManuallySet(false)
      
      console.log('[resetSlotDurationToSystem] Reset complete. New duration:', newSlotDuration, 'Height:', newHeight)
    }, [mentorProfileData, selectedClinic?.id, roomId, chambersData, MIN_HEIGHT, MAX_HEIGHT, BASE_HEIGHT_PER_MINUTE])

    // Expose refresh method to parent component
    // Ref to store scrollToPresentLine function so it can be accessed in useImperativeHandle
    const scrollToPresentLineRef = useRef(null)

    const createCalendarSlotData = useCallback((slotStart, slotEnd) => {
      return {
        startTime: slotStart.format(`YYYY-MM-DDTHH:mm:ss[${decodeURIComponent(timeZone)}]`),
        endTime: slotEnd.format(`YYYY-MM-DDTHH:mm:ss[${decodeURIComponent(timeZone)}]`),
      }
    }, [timeZone])

    const buildCurrentTimeSlotData = useCallback((referenceTime = dayjs()) => {
      const slotDurationInMins =
        Number.isFinite(Number(eachSlotDurationInMins)) && Number(eachSlotDurationInMins) > 0
          ? Number(eachSlotDurationInMins)
          : 30
      const slotStart = dayjs(referenceTime).second(0).millisecond(0)
      const slotEnd = slotStart.add(slotDurationInMins, 'minute')
      return createCalendarSlotData(slotStart, slotEnd)
    }, [createCalendarSlotData, eachSlotDurationInMins])

    // Helper function to find next available slot starting from current time
    const findNextAvailableSlot = useCallback(() => {
      const now = dayjs()
      const currentDate = now.format('DD-MM-YYYY')
      const isToday = currentDate === selectedDate
      const slotDurationInMins =
        Number.isFinite(Number(eachSlotDurationInMins)) && Number(eachSlotDurationInMins) > 0
          ? Number(eachSlotDurationInMins)
          : 30

      // Helper function to check if a slot overlaps with any booked slot
      const checkSlotOverlap = (slotStart, slotEnd) => {
        return bookedSlotsInDay?.some((bookedSlot) => {
          if (!bookedSlot?.startTime || !bookedSlot?.endTime) return false

          const bookedStart = dayjs(bookedSlot?.startTimeForDayCal || bookedSlot?.startTime)
          const bookedEnd = dayjs(bookedSlot?.endTimeForDayCal || bookedSlot?.endTime)

          // Check if slots overlap (not just exact match)
          return slotStart.isBefore(bookedEnd) && slotEnd.isAfter(bookedStart)
        }) || false
      }

      // If viewing today, start from current time; otherwise start from beginning of selected date
      const baseTime = isToday ? now : dayjs(selectedDate, 'DD-MM-YYYY').startOf('day')

      // Round up to next slot boundary based on slot duration
      const minutes = baseTime.minute()
      const roundedMinutes = Math.ceil(minutes / slotDurationInMins) * slotDurationInMins
      let candidateStart = baseTime.minute(roundedMinutes).second(0).millisecond(0)

      // For today, ensure candidate start is after current time
      if (isToday && candidateStart.isBefore(now)) {
        candidateStart = candidateStart.add(slotDurationInMins, 'minute')
      }

      // Ensure candidate start is not in the past (add buffer for today)
      if (isToday && candidateStart.isBefore(now.add(1, 'minute'))) {
        candidateStart = candidateStart.add(slotDurationInMins, 'minute')
      }

      const candidateEnd = candidateStart.add(slotDurationInMins, 'minute')

      // Check if candidate slot overlaps with any booked slot
      if (!checkSlotOverlap(candidateStart, candidateEnd)) {
        return {
          slotData: createCalendarSlotData(candidateStart, candidateEnd),
          source: 'available',
        }
      }

      // If there's overlap, find next available slot by checking slotGroups
      const allAvailableSlots = slotGroups?.flat() || []

      const sortedSlots = [...allAvailableSlots].sort((a, b) => {
        return dayjs(a?.startTime).diff(dayjs(b?.startTime))
      })

      for (const slot of sortedSlots) {
        const slotStart = dayjs(slot?.startTime)
        const slotEnd = dayjs(slot?.endTime)

        if (slotStart.isBefore(candidateStart)) continue
        if (isToday && slotStart.isBefore(now.add(1, 'minute'))) continue

        if (!checkSlotOverlap(slotStart, slotEnd)) {
          return {
            slotData: {
              startTime: slot?.startTime,
              endTime: slot?.endTime,
            },
            source: 'available',
          }
        }
      }

      let nextSlotStart = candidateStart
      const endOfDay = isToday ? now.endOf('day') : dayjs(selectedDate, 'DD-MM-YYYY').endOf('day')

      while (nextSlotStart.isBefore(endOfDay)) {
        const nextSlotEnd = nextSlotStart.add(slotDurationInMins, 'minute')

        if (isToday && nextSlotStart.isBefore(now.add(1, 'minute'))) {
          nextSlotStart = nextSlotStart.add(slotDurationInMins, 'minute')
          continue
        }

        if (!checkSlotOverlap(nextSlotStart, nextSlotEnd)) {
          return {
            slotData: createCalendarSlotData(nextSlotStart, nextSlotEnd),
            source: 'available',
          }
        }

        nextSlotStart = nextSlotStart.add(slotDurationInMins, 'minute')
      }

      // Gracefully fall back to the current time so patient-search booking can still open.
      return {
        slotData: buildCurrentTimeSlotData(now),
        source: 'fallback-current-time',
      }
    }, [
      selectedDate,
      eachSlotDurationInMins,
      bookedSlotsInDay,
      slotGroups,
      createCalendarSlotData,
      buildCurrentTimeSlotData,
    ])

    const resolveBookingSlotForPatientSearch = useCallback(() => {
      const now = dayjs()
      const today = now.format('DD-MM-YYYY')
      const selectedDateMoment = dayjs(selectedDate, 'DD-MM-YYYY', true)
      const nextSlotResolution = findNextAvailableSlot()
      const nextSlotData = nextSlotResolution?.slotData || null
      const nextSlotStart = nextSlotData?.startTime ? dayjs(nextSlotData.startTime) : null
      const selectedDateIsPast =
        selectedDateMoment.isValid() && selectedDateMoment.endOf('day').isBefore(now)
      const nextSlotIsInPast =
        !nextSlotStart?.isValid?.() || nextSlotStart.endOf('minute').isBefore(now)

      if (nextSlotResolution?.source !== 'available' || selectedDateIsPast || nextSlotIsInPast) {
        return {
          slotData: buildCurrentTimeSlotData(now),
          targetDate: today,
          usedCurrentTimeFallback: true,
        }
      }

      return {
        slotData: nextSlotData,
        targetDate: selectedDate,
        usedCurrentTimeFallback: false,
      }
    }, [selectedDate, findNextAvailableSlot, buildCurrentTimeSlotData])

    // Opens the "Search or Create Patient" booking dialog without a pre-selected
    // patient or a specific clicked calendar slot. Used by the "New" button on the
    // Appointments tab (mirrors clicking an empty slot on the Calendar tab).
    const openNewBookingDialog = useCallback(() => {
      const { slotData, targetDate } = resolveBookingSlotForPatientSearch()
      if (!slotData) {
        return { opened: false }
      }
      if (targetDate && targetDate !== selectedDate) {
        setSelectedDate(targetDate)
      }
      setPreSelectedPatientForBooking(null)
      setClickedWantedSlotData(slotData)
      setNewBookingDialogOpen(true)
      return { opened: true, slotData, targetDate }
    }, [resolveBookingSlotForPatientSearch, selectedDate])

    useImperativeHandle(componentRef, () => ({
      refresh: () => {
        if (refreshBookedSlotsRef.current) {
          refreshBookedSlotsRef.current()
        }
      },
      getAndSetBookedSlotsOfSelectedDate: () => {
        if (refreshBookedSlotsRef.current) {
          refreshBookedSlotsRef.current()
        }
      },
      resetSlotDurationToSystem,
      scrollToPresentLine: () => {
        if (scrollToPresentLineRef.current) {
          scrollToPresentLineRef.current()
        }
      },
      openBookingWithPatient: (patient) => {
        const { slotData, targetDate } = resolveBookingSlotForPatientSearch()
        if (!slotData) {
          return { opened: false }
        }
        if (targetDate && targetDate !== selectedDate) {
          setSelectedDate(targetDate)
        }
        setPreSelectedPatientForBooking(patient)
        setClickedWantedSlotData(slotData)
        setNewBookingDialogOpen(true)
        return { opened: true, slotData, targetDate }
      },
      openNewBookingDialog,
    }), [resetSlotDurationToSystem, resolveBookingSlotForPatientSearch, selectedDate, openNewBookingDialog])

    function handleBookedSlotOpenPopover(event) {
      // For support portal, open drawer instead of popover
      if (isSupportPortal) {
        // Don't set anchor, drawer will be opened via clickedBookedSlotData
        return
      }
      setClickedBookedSlotAnchor(event.currentTarget)
    }

    function getHeightFromTopInSlotGroup(timeBlock, clickedWantedSlotData, eachSlotDurationInMins) {
      const startTimeOfTimeBlock = dayjs(timeBlock[0]?.startTime)
      const minutesPassedSinceStartOfTimeBlock = dayjs(clickedWantedSlotData?.startTime)?.diff(
        startTimeOfTimeBlock,
        'minutes',
      )
      return (minutesPassedSinceStartOfTimeBlock / eachSlotDurationInMins) * heightOfEachSlot
    }

    function getHeightFromTopForCurrentTime(timeBlock, eachSlotDurationInMins) {
      const startTimeOfTimeBlock = dayjs(timeBlock[0]?.startTime)
      const minutesPassedSinceStartOfTimeBlock = dayjs()?.diff(startTimeOfTimeBlock, 'minutes')
      return (minutesPassedSinceStartOfTimeBlock / eachSlotDurationInMins) * heightOfEachSlot
    }

    // Helper functions for collapse functionality
    const shouldCollapseTimeBlock = (timeBlock) => {
      if (!timeBlock || timeBlock.length === 0) return false
      
      try {
        const firstSlotTime = dayjs(timeBlock[0]?.startTime)
        if (!firstSlotTime.isValid()) return false
        
        const hour = firstSlotTime.hour()
        const lastSlotTime = dayjs(timeBlock[timeBlock.length - 1]?.endTime)
        if (!lastSlotTime.isValid()) return false
        
        const lastHour = lastSlotTime.hour()
        
        // Early morning: 12 AM (0) to 7 AM
        // Block must start at or after midnight AND end before or at 7 AM
        const isEarlyMorning = hour >= 0 && hour < 7 && lastHour <= 7
        
        // Late night: After 10 PM (22)
        // Block must either:
        // 1. Start at or after 10 PM, OR
        // 2. End after 10 PM (even if it starts before 10 PM)
        const isLateNight = hour >= 22 || lastHour > 22 || (lastHour === 22 && lastSlotTime.minute() > 0)
        
        return isEarlyMorning || isLateNight
      } catch (error) {
        console.error('Error in shouldCollapseTimeBlock:', error, timeBlock)
        return false
      }
    }

    const hasBookingsInTimeBlock = (timeBlock) => {
      if (!bookedSlotsInDay || bookedSlotsInDay.length === 0) return false
      if (!timeBlock || timeBlock.length === 0) return false
      
      const startTimeOfTimeBlock = dayjs(timeBlock[0]?.startTime)
      const endTimeOfTimeBlock = dayjs(timeBlock[timeBlock.length - 1]?.endTime)
      
      return bookedSlotsInDay.some((bookedSlot) => {
        const bookedStart = dayjs(bookedSlot?.startTimeForDayCal || bookedSlot?.startTime)
        return bookedStart.isBetween(startTimeOfTimeBlock, endTimeOfTimeBlock, null, '[]')
      })
    }

    // Helper to check if a section (array of time blocks) has bookings
    const hasBookingsInSection = (sectionBlocks) => {
      if (!bookedSlotsInDay || bookedSlotsInDay.length === 0) return false
      if (!sectionBlocks || sectionBlocks.length === 0) return false
      
      return sectionBlocks.some((timeBlock) => hasBookingsInTimeBlock(timeBlock))
    }

    // Helper to check if a section has bookings (including overflow from other sections)
    const hasBookingsInSectionIncludingOverflow = (sectionBlocks, sectionType) => {
      if (!bookedSlotsInDay || bookedSlotsInDay.length === 0) return false
      if (!sectionBlocks || sectionBlocks.length === 0) return false
      
      // Check for direct bookings
      if (hasBookingsInSection(sectionBlocks)) return true
      
      // Check for overflow from other sections
      return bookedSlotsInDay.some((bookedSlot) => {
        const slotStart = dayjs(bookedSlot?.startTimeForDayCal || bookedSlot?.startTime)
        const slotEnd = dayjs(bookedSlot?.endTimeForDayCal || bookedSlot?.endTime)
        if (!slotStart.isValid() || !slotEnd.isValid()) return false
        
        const slotStartHour = slotStart.hour()
        const slotEndHour = slotEnd.hour()
        const slotEndMinute = slotEnd.minute()
        
        // Check if slot overflows into this section
        if (sectionType === 'earlyMorning') {
          // Slot starts before 7 AM but ends after 7 AM, or starts in early morning
          return (slotStartHour < 7 && slotEndHour >= 7) || (slotStartHour >= 0 && slotStartHour < 7)
        } else if (sectionType === 'workDay') {
          // Slot starts before 7 AM and ends after 7 AM, or starts before 10 PM and ends after 10 PM
          return (slotStartHour < 7 && slotEndHour >= 7) || (slotStartHour < 22 && (slotEndHour > 22 || (slotEndHour === 22 && slotEndMinute > 0)))
        } else if (sectionType === 'lateNight') {
          // Slot starts before 10 PM but ends after 10 PM
          return slotStartHour < 22 && (slotEndHour > 22 || (slotEndHour === 22 && slotEndMinute > 0))
        }
        
        return false
      })
    }

    // Helper to get section label
    const getSectionLabel = (sectionType) => {
      switch (sectionType) {
        case 'earlyMorning':
          return { title: 'Early Morning', subtitle: '12:00 AM - 7:00 AM' }
        case 'workDay':
          return { title: 'Work Day', subtitle: '7:00 AM - 10:00 PM' }
        case 'lateNight':
          return { title: 'Late Night', subtitle: '10:00 PM - 11:59 PM' }
        default:
          return { title: '', subtitle: '' }
      }
    }

    // Helper to render a time section
    const renderTimeSection = (sectionType, sectionBlocks, sectionIndex) => {
      if (!sectionBlocks || sectionBlocks.length === 0) return null
      
      const sectionLabel = getSectionLabel(sectionType)
      const isCollapsed = sectionType === 'earlyMorning' 
        ? collapsedRanges.earlyMorning 
        : sectionType === 'lateNight' 
        ? collapsedRanges.lateNight 
        : false
      // Early morning and late night sections are always collapsible
      const shouldShowCollapse = (sectionType === 'earlyMorning' || sectionType === 'lateNight')
      const shouldRenderCollapsed = shouldShowCollapse && isCollapsed
      
      // Calculate total slots in section
      const totalSlots = sectionBlocks.reduce((sum, block) => sum + (block?.length || 0), 0)
      
      return (
        <div key={`section-${sectionType}-${sectionIndex}`} style={{ width: '100%' }}>
          {/* Section Header - Clickable to collapse when expanded */}
          {shouldShowCollapse && !isCollapsed ? (
            <TimeSectionHeader
              onClick={() => {
                if (sectionType === 'earlyMorning') {
                  setCollapsedRanges((prev) => ({ ...prev, earlyMorning: true }))
                } else if (sectionType === 'lateNight') {
                  setCollapsedRanges((prev) => ({ ...prev, lateNight: true }))
                }
              }}
              style={{ cursor: 'pointer', userSelect: 'none' }}
              role="button"
              tabIndex={0}
              onKeyDown={(e) => {
                if (e.key === 'Enter' || e.key === ' ') {
                  e.preventDefault()
                  if (sectionType === 'earlyMorning') {
                    setCollapsedRanges((prev) => ({ ...prev, earlyMorning: true }))
                  } else if (sectionType === 'lateNight') {
                    setCollapsedRanges((prev) => ({ ...prev, lateNight: true }))
                  }
                }
              }}
            >
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <div>
                  <div className="section-title">{sectionLabel.title}</div>
                  <div className="section-subtitle">{sectionLabel.subtitle} • {totalSlots} slots</div>
                </div>
                <ExpandLess style={{ fontSize: 18, color: 'rgba(67, 56, 202, 0.55)' }} />
              </div>
            </TimeSectionHeader>
          ) : (
            <TimeSectionHeader>
              <div className="section-title">{sectionLabel.title}</div>
              <div className="section-subtitle">{sectionLabel.subtitle} • {totalSlots} slots</div>
            </TimeSectionHeader>
          )}
          
          {/* Collapsed Header or Expanded Content */}
          {shouldRenderCollapsed ? (
            <EDVCCollapsedHeader
              onClick={() => {
                if (sectionType === 'earlyMorning') {
                  setCollapsedRanges((prev) => ({ ...prev, earlyMorning: !prev.earlyMorning }))
                } else if (sectionType === 'lateNight') {
                  setCollapsedRanges((prev) => ({ ...prev, lateNight: !prev.lateNight }))
                }
              }}
              role="button"
              aria-expanded={!isCollapsed}
              tabIndex={0}
              onKeyDown={(e) => {
                if (e.key === 'Enter' || e.key === ' ') {
                  e.preventDefault()
                  if (sectionType === 'earlyMorning') {
                    setCollapsedRanges((prev) => ({ ...prev, earlyMorning: !prev.earlyMorning }))
                  } else if (sectionType === 'lateNight') {
                    setCollapsedRanges((prev) => ({ ...prev, lateNight: !prev.lateNight }))
                  }
                }
              }}
            >
              <span className="collapse-label">
                {sectionLabel.title} ({sectionLabel.subtitle}) - {totalSlots} slots
              </span>
              {isCollapsed ? (
                <ExpandMore className="collapse-icon" style={{ fontSize: 18 }} />
              ) : (
                <ExpandLess className="collapse-icon" style={{ fontSize: 18 }} />
              )}
            </EDVCCollapsedHeader>
          ) : (
            sectionBlocks.map((timeBlock, timeBlockIndex) => {
              // Check if this is the last timeBlock in the last section (lateNight)
              const isLastBlockInLastSection = sectionType === 'lateNight' && 
                timeBlockIndex === sectionBlocks.length - 1
              
              // Get the last slot in this timeBlock
              const lastSlot = timeBlock && timeBlock.length > 0 
                ? timeBlock[timeBlock.length - 1] 
                : null
              
              // Find the absolute last slot across all sections to check if there's a booking there
              let absoluteLastSlot = null
              if (splitSlotsIntoTimeSections && splitSlotsIntoTimeSections.lateNight && splitSlotsIntoTimeSections.lateNight.length > 0) {
                const lastLateNightBlock = splitSlotsIntoTimeSections.lateNight[splitSlotsIntoTimeSections.lateNight.length - 1]
                if (lastLateNightBlock && lastLateNightBlock.length > 0) {
                  absoluteLastSlot = lastLateNightBlock[lastLateNightBlock.length - 1]
                }
              }
              
              // Check if this is the last timeBlock and if there's a booking at the absolute last slot
              const hasBookingAtLastSlot = absoluteLastSlot && 
                isLastBlockInLastSection && 
                lastSlot && 
                dayjs(absoluteLastSlot?.startTime).isSame(dayjs(lastSlot?.startTime), 'minute') &&
                bookedSlotsInDay && bookedSlotsInDay.some((bookedSlot) => {
                  const slotStart = dayjs(bookedSlot?.startTimeForDayCal || bookedSlot?.startTime)
                  const absoluteLastSlotStart = dayjs(absoluteLastSlot?.startTime)
                  return slotStart.isValid() && absoluteLastSlotStart.isValid() && 
                    slotStart.isSame(absoluteLastSlotStart, 'minute')
                })
              
              // Number of dummy slots to add (3 slots)
              const numberOfDummySlots = hasBookingAtLastSlot ? 3 : 0
              
              return (
                <EDVCContainer 
                  key={`${sectionType}-block-${timeBlockIndex}`}
                  className={activeSliderSlotId ? 'dimmed-content' : ''}
                >
                  {/* Render slots for this time block */}
                  {timeBlock?.map((slot, slotIndex) => (
                    <EDVCSlotItemContainer
                      key={`${sectionType}-slot-${slotIndex}`}
                      heightOfEachSlot={heightOfEachSlot}
                    >
                      <EDVCSlotItemTime>
                        <EDVCSlotStartTime style={{ top: '0px' }}>
                          {dayjs(slot?.startTime)?.format('hh:mmA')}
                        </EDVCSlotStartTime>
                      </EDVCSlotItemTime>
                      <EDVCSlotItem
                        heightOfEachSlot={heightOfEachSlot}
                        onClick={() => {
                          if (emptySlotClickFunctionOverride) {
                            emptySlotClickFunctionOverride()
                          } else {
                            setClickedWantedSlotData({
                              endTime: slot?.endTime,
                              startTime: slot?.startTime,
                            })
                            setNewBookingDialogOpen(true)
                          }
                        }}
                      ></EDVCSlotItem>
                    </EDVCSlotItemContainer>
                  ))}
                  
                  {/* Render dummy/untimed slots after last slot if there's a booking at the last slot */}
                  {numberOfDummySlots > 0 && absoluteLastSlot && Array.from({ length: numberOfDummySlots }).map((_, dummyIndex) => (
                    <EDVCSlotItemContainer
                      key={`${sectionType}-dummy-slot-${dummyIndex}`}
                      heightOfEachSlot={heightOfEachSlot}
                    >
                      <EDVCSlotItemTime>
                        {/* No time label for dummy slots */}
                        <EDVCSlotStartTime style={{ opacity: 0, visibility: 'hidden' }}>
                          &nbsp;
                        </EDVCSlotStartTime>
                      </EDVCSlotItemTime>
                      <EDVCSlotItem
                        heightOfEachSlot={heightOfEachSlot}
                        onClick={() => {
                          if (emptySlotClickFunctionOverride) {
                            emptySlotClickFunctionOverride()
                          } else {
                            // Use the absolute last slot's startTime as default
                            const absoluteLastSlotStart = dayjs(absoluteLastSlot?.startTime)
                            const absoluteLastSlotEnd = dayjs(absoluteLastSlot?.endTime)
                            setClickedWantedSlotData({
                              endTime: absoluteLastSlotEnd.format('YYYY-MM-DDTHH:mm:ssZ'),
                              startTime: absoluteLastSlotStart.format('YYYY-MM-DDTHH:mm:ssZ'),
                            })
                            setNewBookingDialogOpen(true)
                          }
                        }}
                        style={{
                          borderStyle: 'dashed',
                          borderColor: 'rgba(67, 56, 202, 0.2)',
                          backgroundColor: 'rgba(67, 56, 202, 0.02)',
                          opacity: 0.6,
                        }}
                        title="Click to book (uses last slot time)"
                      ></EDVCSlotItem>
                    </EDVCSlotItemContainer>
                  ))}
                  
                  {/* Render highlighted slots for this time block */}
                  {highlightedForAvailabilitySlotGroups?.map(
                    (hightlightedBlock, hightlightedBlockIndex) => {
                      const startTimeOfTimeBlock = dayjs(timeBlock[0]?.startTime)
                      const endTimeOfTimeBlock = dayjs(
                        timeBlock[timeBlock?.length - 1]?.endTime,
                      )
                      if (
                        dayjs(hightlightedBlock?.startTime).isBetween(
                          startTimeOfTimeBlock,
                          endTimeOfTimeBlock,
                          null,
                          '[]',
                        )
                      ) {
                        const durationOfHighlightedSlotInMins = dayjs(
                          hightlightedBlock?.endTime,
                        )?.diff(dayjs(hightlightedBlock?.startTime), 'minutes')
                        const minutesPassedSinceStartOfTimeBlockTillThisHighlight = dayjs(
                          hightlightedBlock?.startTime,
                        )?.diff(startTimeOfTimeBlock, 'minutes')
                        return (
                          <HighlightedBlockContainer
                            style={{ background: 'cream' }}
                            key={`${sectionType}-highlight-${hightlightedBlockIndex}`}
                            fromTopInPx={
                              (minutesPassedSinceStartOfTimeBlockTillThisHighlight /
                                eachSlotDurationInMins) *
                              heightOfEachSlot
                            }
                            heightInPx={
                              (durationOfHighlightedSlotInMins / eachSlotDurationInMins) *
                              heightOfEachSlot
                            }
                          >
                            <HighlightedBlockStartMarker
                              ref={(e) => {
                                if (e) {
                                  clinicStartTimeRef.current[
                                    dayjs(hightlightedBlock?.startTime)?.format('h:mm A')
                                  ] = e
                                }
                              }}
                            >
                              CLINIC OPENS{' - '}
                              {dayjs(hightlightedBlock?.startTime).format('h:mm A')}
                            </HighlightedBlockStartMarker>
                            <HighlightedBlockEndMarker>
                              CLINIC CLOSES -{' '}
                              {dayjs(hightlightedBlock?.endTime).format('h:mm A')}
                            </HighlightedBlockEndMarker>
                          </HighlightedBlockContainer>
                        )
                      } else {
                        return null
                      }
                    },
                  )}
                  
                  {/* Render booked slots for this time block */}
                  {(() => {
                    const startTimeOfTimeBlock = dayjs(timeBlock[0]?.startTime)
                    const endTimeOfTimeBlock = dayjs(
                      timeBlock[timeBlock?.length - 1]?.endTime,
                    )
                    const overlapMapForBlock = getOverlapGroupsForTimeBlock(
                      bookedSlotsInDay,
                      startTimeOfTimeBlock,
                      endTimeOfTimeBlock,
                    )
                    return bookedSlotsInDay?.map((bookedSlot, bookedSlotIndex) => {
                      const slotStart = dayjs(bookedSlot?.startTimeForDayCal || bookedSlot?.startTime)
                      const slotEnd = dayjs(bookedSlot?.endTimeForDayCal || bookedSlot?.endTime)
                      
                      // Check if slot overlaps with this time block (including partial overlap)
                      const slotOverlapsBlock = slotStart.isValid() && slotEnd.isValid() &&
                        slotStart.isBefore(endTimeOfTimeBlock) && slotEnd.isAfter(startTimeOfTimeBlock)
                      
                      if (slotOverlapsBlock) {
                        const overlap = overlapMapForBlock.get(bookedSlot?.id) ?? overlapMapForBlock.get(bookedSlot?.uid) ?? { overlapIndex: 0, overlapTotal: 1 }
                        // Calculate the visible portion of the slot in this section
                        const visibleStart = slotStart.isAfter(startTimeOfTimeBlock) ? slotStart : startTimeOfTimeBlock
                        const visibleEnd = slotEnd.isBefore(endTimeOfTimeBlock) ? slotEnd : endTimeOfTimeBlock
                        
                        // Calculate duration and position for the visible portion
                        const visibleDurationInMins = visibleEnd.diff(visibleStart, 'minutes')
                        const minutesPassedSinceStartOfTimeBlock = visibleStart.diff(startTimeOfTimeBlock, 'minutes')
                        
                        // Use the visible duration instead of full duration
                        const durationOfBookedSlotInMins = visibleDurationInMins
                        const pendingActionsForThisBookedEvent = pendingActionsOfPerson?.filter(
                          (item) => item?.bookedEventId === bookedSlot?.id,
                        )
                        const therapyBookingDataForThisEvent =
                          pendingActionsForThisBookedEvent?.find(
                            (pa) => pa?.type === 'schedule-therapy',
                          )
                        const slotBlocked = selectedClinic?.id !== bookedSlot?.clinicId
                        
                        return (
                          <React.Fragment key={`${sectionType}-booked-fragment-${bookedSlotIndex}-${bookedSlot?.id}`}>
                          <BookedSlotItemContent
                            key={`${sectionType}-booked-${bookedSlotIndex}-${bookedSlot?.id}`}
                            overlapIndex={overlap.overlapIndex}
                            overlapTotal={overlap.overlapTotal}
                            patientHistoryGetApiSignature={patientHistoryGetApiSignature}
                          onOpenMainDrawer={(bookedSlot) => {
                            setSelectedBookedSlotForDrawer(bookedSlot)
                            setBookedEventDetailsDrawerOpen(true)
                          }}
                          onClickItem={(event) => {
                            // Allow clicking for mentors and support portal even if from different clinic
                            const isSupportPortal = typeof window !== 'undefined' && window.location?.pathname?.includes('/support')
                            if (slotBlocked && !forMentor && !isSupportPortal) {
                              return
                            }
                            // CRITICAL: For mentor portal, only allow opening session page for booked status
                            // Events/Bookings which do not have status=booked cannot be opened (session page cannot be opened)
                            // Only mentor portal has the session opening functionality
                            const isBooked = bookedSlot?.status === STATUS_SESSION_BOOKED
                            const isCompleted = bookedSlot?.status === STATUS_SESSION_COMPLETED
                            const isBlockedForPayment = 
                              bookedSlot?.status === BOOKING_STATUSES.BOOKING_INITIATED &&
                              bookedSlot?.paymentStatus === BOOKING_PAYMENT_STATUSES.PENDING
                            const isCancelled = bookedSlot?.status === BOOKING_SESSION_STATUS.CANCELLED
                            
                            // For mentor portal: Only allow booked status to open session (prevent non-booked from opening)
                            // For support portal: Allow any non-cancelled slot to open drawer
                            // For other portals: Allow booked/completed or blocked for payment
                            const shouldOpenDrawer = 
                              (isSupportPortal && !isCancelled) ||
                              (forMentor ? isBooked : (isBooked || isCompleted)) ||
                              (isBlockedForPayment && forMentor)
                            
                            if (!shouldOpenDrawer) {
                              return // Don't open drawer/session page
                            }
                            
                            if (bookedSlotClickFunctionOverride) {
                              // For mentor portal, ensure only booked status can trigger session opening
                              if (forMentor && !isBooked && !isBlockedForPayment) {
                                return // Prevent non-booked events from opening session in mentor portal
                              }
                              bookedSlotClickFunctionOverride(bookedSlot)
                            } else {
                              setClickedBookedSlotData(bookedSlot)
                              if (isSupportPortal) {
                                // Open drawer for support portal
                                setSelectedBookedSlotForDrawer(bookedSlot)
                                setBookedEventDetailsDrawerOpen(true)
                              } else {
                                // Use popover for other portals
                                // For mentor portal, only allow if booked (session opening)
                                if (forMentor) {
                                  if (!isBooked && !isBlockedForPayment) {
                                    return // Prevent non-booked events from opening session in mentor portal
                                  }
                                  handleBookedSlotOpenPopover(event)
                                  if (fireCheckInApiOnCalendarItemClick) {
                                    checkIntoCurrentlyPeekedInRoomAsInPerson({
                                      currentClinic: selectedClinic,
                                    })
                                  }
                                } else {
                                  handleBookedSlotOpenPopover(event)
                                }
                              }
                            }
                          }}
                          showPatientHistoryInUploadPrescriptionWidget={
                            showPatientHistoryInUploadPrescriptionWidget
                          }
                          mentorProfileData={mentorProfileData}
                          refreshBookedSlots={getAndSetBookedSlotsOfSelectedDate}
                          durationOfBookedSlotInMins={durationOfBookedSlotInMins}
                          eachSlotDurationInMins={eachSlotDurationInMins}
                          heightOfEachSlot={heightOfEachSlot}
                          showViewReportsButtonInActionButtons={
                            showViewReportsButtonInActionButtons
                          }
                          openReportViewDrawer={(slot) => {
                            setSelectedBookedSlotForReport(slot)
                            setReportViewDrawerOpen(true)
                          }}
                          minutesPassedSinceStartOfTimeBlock={
                            minutesPassedSinceStartOfTimeBlock
                          }
                          prescriptionGetApiSignature={getPrescriptionImagesApiSignature}
                          prescriptionSaveApiSignature={savePrescriptionImagesApiSignature}
                          bookedSlot={bookedSlot}
                          hidePatientContactDetails={hidePatientContactDetails}
                          canPersonPrintPrescription={canPersonPrintPrescription}
                          forMentor={forMentor}
                          handleNewTherapyRequest={handleNewTherapyRequest}
                          hidePreviousTherapyReportsButton={hidePreviousTherapyReportsButton}
                          minimalUI={minimalUI}
                          openReceiptDialog={openReceiptDialog}
                          pendingActionsForThisBookedEvent={pendingActionsForThisBookedEvent}
                          setAddVitalsDialogOpen={setAddVitalsDialogOpen}
                          setClickedBookedSlotData={setClickedBookedSlotData}
                          setEditBookingDialogOpen={setEditBookingDialogOpen}
                          setEditPatientDialogOpen={setEditPatientDialogOpen}
                          setEventDataForReceiptPrinting={setEventDataForReceiptPrinting}
                          setPendingActionsDialogOpen={setPendingActionsDialogOpen}
                          fileUploadApiSignature={fileUploadApiSignature}
                          showGalleryForPrescriptionImages={showGalleryForPrescriptionImages}
                          slotBlocked={slotBlocked}
                          sendPrescriptionPdfApiSignature={sendPrescriptionPdfApiSignature}
                          hideRescheduleDialog={hideRescheduleDialog}
                          canRescheduleBookedSlot={canRescheduleBookedSlot}
                          rescheduleDialogType={rescheduleDialogType}
                          forceShowJoiningDetailsActionButton={
                            forceShowJoiningDetailsActionButton
                          }
                          prescriptionImagesGetApiSignature={
                            getPrescriptionImagesApiSignature
                          }
                          prescriptionImagesSaveApiSignature={
                            savePrescriptionImagesApiSignature
                          }
                          vitalsFormGetApiSignature={vitalsFormGetApiSignature}
                          clinicBrandData={
                            slotBlocked && (forMentor || window.location?.pathname?.includes('/support'))
                              ? bookedSlot?.clinicBrandData
                              : selectedClinicBrandData
                          }
                          clinicLocationData={
                            slotBlocked && (forMentor || window.location?.pathname?.includes('/support'))
                              ? bookedSlot?.clinicLocation || bookedSlot?.clinicLocationData
                              : selectedClinic
                          }
                          clinicAssociationsData={getClinicDataFromAssociations(bookedSlot?.clinicId, bookedSlot?.clinicBrandId)}
                          clinicAssociations={clinicAssociations}
                          showPrescriptionPdfDownloadButtons={shouldShowPrescriptionPdfDownloadButtons(
                            {
                              forMentor,
                              bookedSlot,
                              prescriptionViewSettingsData:
                                prescrpitionViewSettingsStoreData?.data,
                            },
                          )}
                          slotContainerRef={(e) => {
                            if (e) {
                              bookedEventsRef.current.set(bookedSlot, e)
                            } else {
                              bookedEventsRef.current.delete(bookedSlot)
                            }
                          }}
                          therapyBookingDataForThisEvent={therapyBookingDataForThisEvent}
                          fireCheckInApiOnCalendarItemClick={
                            fireCheckInApiOnCalendarItemClick
                          }
                          sendPublicSessionLinkToPatientSignature={
                            sendPublicSessionLinkToPatientSignature
                          }
                          sendSecureSessionLinkToPatientSignature={
                            sendSecureSessionLinkToPatientSignature
                          }
                          includeReportRelatedData={includeReportRelatedData}
                          bookedSlotsInDay={bookedSlotsInDay}
                          onGoToEvent={goToEvent}
                        />
                          </React.Fragment>
                        )
                      } else {
                        return null
                      }
                    })
                  })()}
                  
                  {/* Floating buttons for small booked slots - rendered after all slot items */}
                  {bookedSlotsInDay?.map((bookedSlot, bookedSlotIndex) => {
                    const startTimeOfTimeBlock = dayjs(timeBlock[0]?.startTime)
                    const endTimeOfTimeBlock = dayjs(timeBlock[timeBlock?.length - 1]?.endTime)
                    const slotStart = dayjs(bookedSlot?.startTimeForDayCal || bookedSlot?.startTime)
                    const slotEnd = dayjs(bookedSlot?.endTimeForDayCal || bookedSlot?.endTime)
                    
                    const slotOverlapsBlock = slotStart.isValid() && slotEnd.isValid() &&
                      slotStart.isBefore(endTimeOfTimeBlock) && slotEnd.isAfter(startTimeOfTimeBlock)
                    
                    if (slotOverlapsBlock) {
                      const visibleStart = slotStart.isAfter(startTimeOfTimeBlock) ? slotStart : startTimeOfTimeBlock
                      const visibleEnd = slotEnd.isBefore(endTimeOfTimeBlock) ? slotEnd : endTimeOfTimeBlock
                      const visibleDurationInMins = visibleEnd.diff(visibleStart, 'minutes')
                      const minutesPassedSinceStartOfTimeBlock = visibleStart.diff(startTimeOfTimeBlock, 'minutes')
                      const slotHeight = (visibleDurationInMins / eachSlotDurationInMins) * heightOfEachSlot
                      // REQUIREMENT: Double the height - threshold doubled from 60 to 120
                      const isSmallSlot = slotHeight < 120
                      const slotTopPosition = (minutesPassedSinceStartOfTimeBlock / eachSlotDurationInMins) * heightOfEachSlot
                      
                      // Don't automatically show slider - removed for now
                      // Slider functionality can be re-enabled later if needed
                      return null
                    }
                    return null
                  })}
                  
                  {/* Current time line for this time block - rendered after all slot items */}
                  {(() => {
                    // Guard: Ensure timeBlock exists and has items
                    if (!timeBlock || timeBlock.length === 0 || !timeBlock[0]?.startTime) {
                      return null
                    }
                    
                    const currentTimeInClinicTZ = dayjs()
                    const currentTimeFormatted = currentTimeInClinicTZ.format('h:mm A')
                    const startTimeOfTimeBlock = dayjs(timeBlock[0]?.startTime)
                    const endTimeOfTimeBlock = dayjs(
                      timeBlock[timeBlock?.length - 1]?.endTime,
                    )
                    
                    // Guard: Ensure times are valid
                    if (!startTimeOfTimeBlock.isValid() || !endTimeOfTimeBlock.isValid()) {
                      return null
                    }
                    
                    if (
                      currentTimeInClinicTZ.isBetween(
                        startTimeOfTimeBlock,
                        endTimeOfTimeBlock,
                        null,
                        '[]',
                      )
                    ) {
                      const fromTopInPx = getHeightFromTopForCurrentTime(
                        timeBlock,
                        eachSlotDurationInMins,
                      )
                      
                      return (
                        <div 
                          className="time-line-wrapper" 
                          style={{ 
                            position: 'absolute',
                            top: 0,
                            left: 0,
                            right: 0,
                            bottom: 0,
                            pointerEvents: 'none',
                            zIndex: 5
                          }}
                        >
                          <EDVCPresentTimeLine ref={presentTimeLineRef} fromTopInPx={fromTopInPx}>
                            <EDVCPresentTimeLineDot />
                          </EDVCPresentTimeLine>
                          <EDVCPresentTimeTag
                            fromTopInPx={fromTopInPx}
                            className="time-tag"
                          >
                            {currentTimeFormatted}
                          </EDVCPresentTimeTag>
                        </div>
                      )
                    }
                    return null
                  })()}
                  
                  {/* Extra slot item for time if current time is before first slot */}
                  {timeBlockIndex === 0 && sectionIndex === 0 && 
                   timeBlock && timeBlock.length > 0 && timeBlock[0]?.startTime &&
                   dayjs().isBetween(
                    dayjs(slotGroups?.[0]?.[0]?.startTime, 'YYYY-MM-DDTHH:mm:ssZ').startOf('day'),
                    dayjs(timeBlock[0]?.startTime),
                    null,
                    '[]',
                  ) && (
                    <EDVCExtraSlotItemForTime>
                      <div className="time-line-wrapper" style={{ position: 'relative' }}>
                        <EDVCPresentTimeLine ref={presentTimeLineRef} fromTopInPx={0}>
                          <EDVCPresentTimeLineDot />
                        </EDVCPresentTimeLine>
                        <EDVCPresentTimeTag
                          fromTopInPx={0}
                          className="time-tag"
                        >
                          {dayjs().format('h:mm A')}
                        </EDVCPresentTimeTag>
                      </div>
                      <EDVCSlotItemTime>
                        <EDVCSlotStartTime style={{ top: 'unset', right: 'unset' }}>
                          {dayjs()?.format('hh:mmA')}
                        </EDVCSlotStartTime>
                      </EDVCSlotItemTime>
                    </EDVCExtraSlotItemForTime>
                  )}
                  
                  {/* Extra slot item for time if current time is after last slot */}
                  {timeBlockIndex === sectionBlocks.length - 1 && 
                   sectionIndex === 2 && // lateNight is the last section
                   timeBlock && timeBlock.length > 0 && timeBlock[timeBlock?.length - 1]?.endTime &&
                   dayjs().isBetween(
                    dayjs(timeBlock[timeBlock?.length - 1]?.endTime, 'YYYY-MM-DDTHH:mm:ssZ'),
                    dayjs(
                      timeBlock[timeBlock?.length - 1]?.endTime,
                      'YYYY-MM-DDTHH:mm:ssZ',
                    ).endOf('day'),
                    null,
                    '[]',
                  ) && (
                    <EDVCExtraSlotItemForTime>
                      <div className="time-line-wrapper" style={{ position: 'relative' }}>
                        <EDVCPresentTimeLine ref={presentTimeLineRef} fromTopInPx={0}>
                          <EDVCPresentTimeLineDot />
                        </EDVCPresentTimeLine>
                        <EDVCPresentTimeTag
                          fromTopInPx={0}
                          className="time-tag"
                        >
                          {dayjs().format('h:mm A')}
                        </EDVCPresentTimeTag>
                      </div>
                      <EDVCSlotItemTime>
                        <EDVCSlotStartTime style={{ top: 'unset', right: 'unset' }}>
                          {dayjs()?.format('hh:mmA')}
                        </EDVCSlotStartTime>
                      </EDVCSlotItemTime>
                    </EDVCExtraSlotItemForTime>
                  )}
                </EDVCContainer>
              )
            })
          )}
        </div>
      )
    }

    const getCollapseLabel = (timeBlock) => {
      if (!timeBlock || timeBlock.length === 0) return ''
      
      try {
        const firstSlotTime = dayjs(timeBlock[0]?.startTime)
        if (!firstSlotTime.isValid()) return ''
        
        const lastSlotTime = dayjs(timeBlock[timeBlock.length - 1]?.endTime)
        if (!lastSlotTime.isValid()) return ''
        
        const hour = firstSlotTime.hour()
        const lastHour = lastSlotTime.hour()
        const firstTimeStr = firstSlotTime.format('h:mm A')
        const lastTimeStr = lastSlotTime.format('h:mm A')
        
        // Early morning: check if block is entirely in 0-7 AM range
        if (hour >= 0 && hour < 7 && lastHour <= 7) {
          return `Early Morning (${firstTimeStr} - ${lastTimeStr}) - ${timeBlock.length} slots`
        } else if (hour >= 22) {
          // Late night: check if block starts at or after 10 PM
          return `Late Night (${firstTimeStr} - ${lastTimeStr}) - ${timeBlock.length} slots`
        }
        
        return `${timeBlock.length} slots`
      } catch (error) {
        console.error('Error in getCollapseLabel:', error)
        return `${timeBlock.length} slots`
      }
    }

    const isTimeBlockCollapsed = (timeBlock) => {
      if (!shouldCollapseTimeBlock(timeBlock)) return false
      if (hasBookingsInTimeBlock(timeBlock)) return false // Don't collapse if there are bookings
      
      try {
        const firstSlotTime = dayjs(timeBlock[0]?.startTime)
        if (!firstSlotTime.isValid()) return false
        
        const hour = firstSlotTime.hour()
        const lastSlotTime = dayjs(timeBlock[timeBlock.length - 1]?.endTime)
        if (!lastSlotTime.isValid()) return false
        const lastHour = lastSlotTime.hour()
        
        // Early morning: check if block is entirely in 0-7 AM range
        if (hour >= 0 && hour < 7 && lastHour <= 7) {
          return collapsedRanges.earlyMorning
        }
        // Late night: check if block starts at or after 10 PM, or ends after 10 PM
        const lastMinuteForLateNight = lastSlotTime.minute()
        if (hour >= 22 || lastHour > 22 || (lastHour === 22 && lastMinuteForLateNight > 0)) {
          return collapsedRanges.lateNight
        }
        
        return false
      } catch (error) {
        console.error('Error in isTimeBlockCollapsed:', error)
        return false
      }
    }

    const handleToggleCollapse = (timeBlock) => {
      try {
        const firstSlotTime = dayjs(timeBlock[0]?.startTime)
        if (!firstSlotTime.isValid()) return
        
        const hour = firstSlotTime.hour()
        const lastSlotTime = dayjs(timeBlock[timeBlock.length - 1]?.endTime)
        if (!lastSlotTime.isValid()) return
        const lastHour = lastSlotTime.hour()
        
        // Early morning: check if block is entirely in 0-7 AM range
        if (hour >= 0 && hour < 7 && lastHour <= 7) {
          setCollapsedRanges((prev) => ({ ...prev, earlyMorning: !prev.earlyMorning }))
        } else if (hour >= 22 || lastHour > 22 || (lastHour === 22 && lastSlotTime.minute() > 0)) {
          // Late night: check if block starts at or after 10 PM, or ends after 10 PM
          setCollapsedRanges((prev) => ({ ...prev, lateNight: !prev.lateNight }))
        }
      } catch (error) {
        console.error('Error in handleToggleCollapse:', error)
      }
    }

    const expandRangeIfNeeded = (targetTime) => {
      const targetMoment = dayjs(targetTime)
      const hour = targetMoment.hour()
      
      if (hour >= 0 && hour < 7 && collapsedRanges.earlyMorning) {
        setCollapsedRanges((prev) => ({ ...prev, earlyMorning: false }))
      } else if (hour >= 22 && collapsedRanges.lateNight) {
        setCollapsedRanges((prev) => ({ ...prev, lateNight: false }))
      }
    }

    async function getAndSetClinicBrandData(clinicBrandId) {
      setSelectedClinicBrandData()
      const response = await getCPDataWithoutParams(
        `/api/external/clinic/brand-and-clinics?id=${clinicBrandId}`,
      )
      if (response?.status === 200 && isCommonResponseSuccessful(response?.data?.code)) {
        setSelectedClinicBrandData(transformClinicBrandData(response?.data?.data?.clinicBrand))
      }
    }

    function setEventIdForReceiptPrinting(eventId) {
      setDataForReceiptPrinting((prev) => ({
        ...prev,
        eventId: eventId,
        eventData: prev?.eventData?.id !== eventId ? null : prev?.eventData,
      }))
    }

    function setEventDataForReceiptPrinting(eventData) {
      setDataForReceiptPrinting((prev) => ({
        ...prev,
        eventId: eventData?.id,
        eventData: eventData,
      }))
    }

    function openReceiptDialog() {
      setOpenReceiptPrintingDialog(true)
    }
    function closeReceiptDialog() {
      setOpenReceiptPrintingDialog(false)
    }

    // Fetch clinic associations if not available (for support portal/mentor)
    useEffect(() => {
      const isSupportPortal = typeof window !== 'undefined' && window.location?.pathname?.includes('/support')
      const shouldFetchAssociations = (forMentor || isSupportPortal) && 
        (!mentorClinicAssociationData || !Array.isArray(mentorClinicAssociationData) || mentorClinicAssociationData.length === 0) &&
        mentorProfileData?.slug
      
      if (shouldFetchAssociations) {
        async function fetchClinicAssociations() {
          try {
            const resp = await getSPDataWithParams('/api/secure/cc/support/therapists/clinic-associations', {
              'mentor-slug': mentorProfileData?.slug,
            })
            if (resp && isCommonResponseSuccessfulV2(resp)) {
              const clinics = getDataMapFromCommonResponse(resp)?.clinic || []
              setClinicAssociations(clinics)
            }
          } catch (error) {
            console.error('Error fetching clinic associations:', error)
            setClinicAssociations([])
          }
        }
        fetchClinicAssociations()
      } else if (mentorClinicAssociationData && Array.isArray(mentorClinicAssociationData) && mentorClinicAssociationData.length > 0) {
        // Use provided mentorClinicAssociationData if available
        setClinicAssociations(mentorClinicAssociationData)
      }
    }, [forMentor, mentorClinicAssociationData, mentorProfileData?.slug])

    // Helper function to get clinic data from associations
    const getClinicDataFromAssociations = useMemo(() => {
      return (clinicId, clinicBrandId) => {
        const clinicAssociation = clinicAssociations.find(
          (clinic) => clinic?.id === clinicId || clinic?.clinicId === clinicId
        )
        if (clinicAssociation) {
          return {
            clinicBrandName: clinicAssociation?.clinicBrandName,
            clinicLocationName: clinicAssociation?.title, // title is the clinic location name
            clinicBrandId: clinicAssociation?.clinicBrandId || clinicBrandId,
            clinicId: clinicAssociation?.id || clinicAssociation?.clinicId || clinicId,
          }
        }
        return null
      }
    }, [clinicAssociations])

    useEffect(() => {
      getImportedFromItems()
      if (forMentor) {
        dispatch(fetchPrescriptionViewSettings())
      }
    }, [])
    

    useEffect(() => {
      if (!isViewingCalendarRelatedTab) {
        return
      }
      
      // Delay initial API call until after initial render to improve page load
      // Use requestIdleCallback when available, otherwise setTimeout
      const loadInitialData = () => {
        getAndSetBookedSlotsOfSelectedDate()
      }
      
      // Delay initial load to allow page to render first
      if ('requestIdleCallback' in window) {
        window.requestIdleCallback(loadInitialData, { timeout: 1000 })
      } else {
        setTimeout(loadInitialData, 100)
      }
      
      // Set up interval for periodic updates (every 75 seconds)
      const intervalId = setInterval(() => {
        getAndSetBookedSlotsOfSelectedDate()
      }, 75000)
      
      return () => {
        clearInterval(intervalId)
      }
    }, [selectedDate, mentorId, showCancelledOnly, currentView, includeReportRelatedData, getAndSetBookedSlotsOfSelectedDate])

    useEffect(() => {
      ;(async () => {
        const eventDataForSelectedEventType = mentorProfileData?.eventTypes?.find(
          (eventType) => eventType?.uid === selectedEventTypeId,
        )
        if (eventDataForSelectedEventType && !eventDataForSelectedEventType?.free) {
          const response = await getSubscriptionPlanById(
            eventDataForSelectedEventType?.subscriptionPlanId,
          )
          if (response.status === 200) {
            setPlanDataForSelectedEventId(response?.data)
          }
        } else {
          setPlanDataForSelectedEventId(null)
        }
      })()
    }, [selectedEventTypeId])

    // Update slot duration when selected event type changes
    // NOTE: This is now handled by the check-in based calculation in the main useEffect
    // Keeping this for backward compatibility but it should not override check-in based calculation
    useEffect(() => {
      // Skip automatic update if user has manually set the duration
      if (userManuallySetSlotDurationRef.current) {
        return
      }
      
      // Skip if we have check-in details (check-in based calculation takes precedence)
      try {
        const checkedInDetails = localStorage.getItem('checkedInDetails')
        if (checkedInDetails) {
          const parsed = JSON.parse(checkedInDetails)
          if (parsed?.selectedCheckInEventOption) {
            // Check-in based calculation will handle this, skip this useEffect
            return
          }
        }
      } catch (error) {
        // Ignore errors
      }
      
      const eventDataForSelectedEventType = mentorProfileData?.eventTypes?.find(
        (eventType) => eventType?.uid === selectedEventTypeId,
      )
      if (eventDataForSelectedEventType?.durationInMins) {
        // Allow minimum of 5 mins (not 15) to support shorter event types like screening
        const newSlotDuration = Math.max(eventDataForSelectedEventType.durationInMins, 5)
        setEachSlotDurationInMins(newSlotDuration)
        
        // Recalculate slot height when duration changes using the comprehensive calculation
        // But only if user hasn't manually set the slot duration
        if (!userManuallySetSlotDurationRef.current) {
          const calculatedHeight = calculateOptimalSlotHeight()
          setHeightOfEachSlot(calculatedHeight)
          setIsSlotDurationManuallySet(false)
        }
      }
    }, [selectedEventTypeId, mentorProfileData, calculateOptimalSlotHeight])
    
    // Recalculate slot height whenever booked slots change (e.g., when a new booking is added)
    // But only if user hasn't manually set the slot duration
    useEffect(() => {
      if (!userManuallySetSlotDurationRef.current) {
        const calculatedHeight = calculateOptimalSlotHeight()
        setHeightOfEachSlot(calculatedHeight)
        setIsSlotDurationManuallySet(false)
      }
    }, [bookedSlotsInDay, calculateOptimalSlotHeight])

    // Auto-expand collapsed sections if they have bookings (only once on initial load)
    useEffect(() => {
      if (hasAutoExpandedRef.current) return // Only run once
      if (!bookedSlotsInDay || bookedSlotsInDay.length === 0) return
      
      // Check early morning section (including overflow)
      if (splitSlotsIntoTimeSections.earlyMorning.length > 0) {
        const hasBookings = hasBookingsInSectionIncludingOverflow(splitSlotsIntoTimeSections.earlyMorning, 'earlyMorning')
        if (hasBookings && collapsedRanges.earlyMorning) {
          setCollapsedRanges((prev) => ({ ...prev, earlyMorning: false }))
        }
      }
      
      // Check late night section (including overflow)
      if (splitSlotsIntoTimeSections.lateNight.length > 0) {
        const hasBookings = hasBookingsInSectionIncludingOverflow(splitSlotsIntoTimeSections.lateNight, 'lateNight')
        if (hasBookings && collapsedRanges.lateNight) {
          setCollapsedRanges((prev) => ({ ...prev, lateNight: false }))
        }
      }
      
      // Also check for overflow cases even if section has no direct slots
      // Check if any slot overflows into late night
      const hasOverflowToLateNight = bookedSlotsInDay.some((bookedSlot) => {
        const slotStart = dayjs(bookedSlot?.startTimeForDayCal || bookedSlot?.startTime)
        const slotEnd = dayjs(bookedSlot?.endTimeForDayCal || bookedSlot?.endTime)
        if (!slotStart.isValid() || !slotEnd.isValid()) return false
        
        const slotStartHour = slotStart.hour()
        const slotEndHour = slotEnd.hour()
        const slotEndMinute = slotEnd.minute()
        
        // Slot starts before 10 PM but ends after 10 PM
        return slotStartHour < 22 && (slotEndHour > 22 || (slotEndHour === 22 && slotEndMinute > 0))
      })
      
      if (hasOverflowToLateNight && collapsedRanges.lateNight) {
        setCollapsedRanges((prev) => ({ ...prev, lateNight: false }))
      }
      
      // Check if any slot overflows into early morning
      const hasOverflowToEarlyMorning = bookedSlotsInDay.some((bookedSlot) => {
        const slotStart = dayjs(bookedSlot?.startTimeForDayCal || bookedSlot?.startTime)
        const slotEnd = dayjs(bookedSlot?.endTimeForDayCal || bookedSlot?.endTime)
        if (!slotStart.isValid() || !slotEnd.isValid()) return false
        
        const slotStartHour = slotStart.hour()
        const slotEndHour = slotEnd.hour()
        
        // Slot starts before 7 AM but ends after 7 AM, or starts in early morning
        return (slotStartHour < 7 && slotEndHour >= 7) || (slotStartHour >= 0 && slotStartHour < 7)
      })
      
      if (hasOverflowToEarlyMorning && collapsedRanges.earlyMorning) {
        setCollapsedRanges((prev) => ({ ...prev, earlyMorning: false }))
      }
      
      // Mark as auto-expanded after first run
      hasAutoExpandedRef.current = true
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [bookedSlotsInDay, splitSlotsIntoTimeSections])

    // Auto-expand the section that contains the current time line
    // This runs whenever data refreshes to ensure the current time section is always visible
    useEffect(() => {
      if (!splitSlotsIntoTimeSections) return
      
      const currentHour = dayjs().hour()
      
      // Determine which section the current time falls into
      // Early Morning: 12 AM (0:00) to 7 AM (6:59)
      // Work Day: 7 AM (7:00) to 10 PM (21:59) - always expanded, no need to check
      // Late Night: 10 PM (22:00) to 11:59 PM (23:59)
      
      let sectionToExpand = null
      
      if (currentHour >= 0 && currentHour < 7) {
        // Current time is in early morning section (12 AM to 6:59 AM)
        sectionToExpand = 'earlyMorning'
      } else if (currentHour >= 22) {
        // Current time is in late night section (10 PM onwards, i.e., 22:00 to 23:59)
        sectionToExpand = 'lateNight'
      }
      
      // Expand the section if it's collapsed and has slots
      if (sectionToExpand === 'earlyMorning') {
        if (
          splitSlotsIntoTimeSections.earlyMorning.length > 0 &&
          collapsedRanges.earlyMorning
        ) {
          setCollapsedRanges((prev) => ({ ...prev, earlyMorning: false }))
        }
      } else if (sectionToExpand === 'lateNight') {
        if (
          splitSlotsIntoTimeSections.lateNight.length > 0 &&
          collapsedRanges.lateNight
        ) {
          setCollapsedRanges((prev) => ({ ...prev, lateNight: false }))
        }
      }
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [splitSlotsIntoTimeSections, bookedSlotsInDay, slotGroups])

    useEffect(() => {
      let clinicRelatedEventTypes = getAllClinicRelatedEventTypes(
        mentorProfileData?.eventTypes,
        selectedClinic?.id,
      )

      console.info({ nonScreeningEventsInClinic: clinicRelatedEventTypes })

      setEventTypesVisible(clinicRelatedEventTypes)

      const currentlySelectedEvent = getInitiallySelectedEventType(
        clinicRelatedEventTypes,
        thisClinicData?.id,
        true,
      )
      console.log({ currentlySelectedEvent })

      setSelectedEventTypeId(currentlySelectedEvent?.uid)

      setSelectedVisitEventType(currentlySelectedEvent?.visitEventType)

      // Use check-in based slot duration calculation
      // Skip automatic update if user has manually set the duration
      if (!userManuallySetSlotDurationRef.current) {
        // Get check-in event option from localStorage
        let checkInEventOptionId = null
        let checkInEventOptions = null
        let roomDataEventTypes = null
        
        try {
          const checkedInDetails = localStorage.getItem('checkedInDetails')
          if (checkedInDetails) {
            const parsed = JSON.parse(checkedInDetails)
            checkInEventOptionId = parsed?.selectedCheckInEventOption
          }
        } catch (error) {
          console.error('Error reading check-in details:', error)
        }

        // Try to get checkInEventOptions from chambersData if roomId is available
        if (roomId && chambersData && Array.isArray(chambersData)) {
          const currentRoom = chambersData.find((room) => room?.id === roomId)
          if (currentRoom?.checkInEventOptions) {
            checkInEventOptions = currentRoom.checkInEventOptions
          }
        }

        // Get roomDataEventTypes from localStorage
        try {
          const storedRoomDataEventTypes = localStorage.getItem('roomDataEventTypes')
          if (storedRoomDataEventTypes) {
            roomDataEventTypes = JSON.parse(storedRoomDataEventTypes)
            console.log('[DayViewCalendarComponent] Loaded roomDataEventTypes:', roomDataEventTypes.length, 'event types')
          }
        } catch (error) {
          console.error('Error reading roomDataEventTypes:', error)
        }

        // Calculate slot duration based on check-in event type
        const slotDuration = getSlotDurationBasedOnCheckIn(
          mentorProfileData,
          selectedClinic?.id,
          checkInEventOptionId,
          checkInEventOptions,
          roomDataEventTypes,
        )
        // Allow minimum of 5 mins (not 15) to support shorter event types like screening
        const newSlotDuration = Math.max(slotDuration || 15, 5)
        setEachSlotDurationInMins(newSlotDuration)
        setIsSlotDurationManuallySet(false)
      }
      
      // Recalculate slot height when duration changes using the comprehensive calculation
      // Note: This will be recalculated by the useEffect that watches bookedSlotsInDay
    }, [mentorProfileData, selectedClinic?.id, roomId, chambersData])

    function handleSignalingEvent(event) {
      const command = event?.detail?.command
      const payload = event?.detail?.payload
      console.log('handleSignalingEvent', command, payload)
      switch (command) {
        case SIGNALING_COMMANDS.REFRESH_BOOKINGS:
          getAndSetBookedSlotsOfSelectedDate()
          break
        default:
          break
      }
    }

    useEffect(() => {
      if (!forMentor) {
        return undefined
      }

      return addWindowSignalingEventListener({
        dispatchEventKey: DISPATCH_EVENT_KEYS.mentorSignalingEvent,
        handler: handleSignalingEvent,
      })
    }, [forMentor])


    // useEffect(() => {
    //   if (presentTimeLineRef.current) {
    //     const elementRect = presentTimeLineRef.current.getBoundingClientRect()
    //     const scrollPosition = elementRect.top - 300
    //     window.scrollTo({
    //       top: scrollPosition,
    //       behavior: 'smooth',
    //     })
    //   }
    // }, [selectedEventTypeId])

    const scrollToPresentLine = useCallback(async () => {
      await sleep(400)
      if (presentTimeLineRef?.current) {
        if (containerRefForAutoScrolling?.current) {
          const container = containerRefForAutoScrolling.current
          const elementRect = presentTimeLineRef.current.getBoundingClientRect()
          const containerRect = container.getBoundingClientRect()

          // Calculate the scroll position relative to the container
          const scrollTop = container.scrollTop + (elementRect.top - containerRect.top) - 300 // Adjust offset as needed

          // const scrollPosition = elementRect.top - 300

          containerRefForAutoScrolling.current.scrollTo({
            top: scrollTop,
            behavior: 'smooth',
          })
        } else {
          presentTimeLineRef.current.scrollIntoView({ behavior: 'smooth', block: 'center' })
          // window.scrollTo({
          //   top: scrollTop,
          //   behavior: 'smooth',
          // })
        }
      }
    }, [containerRefForAutoScrolling])

    // Store function in ref for useImperativeHandle
    useEffect(() => {
      scrollToPresentLineRef.current = scrollToPresentLine
    }, [scrollToPresentLine])

    function scrollToSlot(slotRef, targetTime = null) {
      const element = slotRef
      if (element) {
        // Expand collapsed range if target time is provided
        if (targetTime) {
          expandRangeIfNeeded(targetTime)
        }
        
        if (containerRefForAutoScrolling?.current) {
          // If containerRefForAutoScrolling exists, scroll within it
          const container = containerRefForAutoScrolling.current
          const elementRect = element.getBoundingClientRect()
          const containerRect = container.getBoundingClientRect()

          // Calculate the scroll position relative to the container
          const scrollTop = container.scrollTop + (elementRect.top - containerRect.top) - 300 // Adjust offset as needed

          container.scrollTo({
            top: scrollTop,
            behavior: 'smooth',
          })
        } else {
          // Fallback to window scrolling
          element.scrollIntoView({ behavior: 'smooth', block: 'center' })
        }
      }
    }
    function scrollToTop() {
      if (containerRefForAutoScrolling?.current) {
        // If containerRefForAutoScrolling exists, scroll within it
        containerRefForAutoScrolling.current.scrollTo({
          top: 0,
          behavior: 'smooth',
        })
      } else {
        // Fallback to window scrolling
        window.scrollTo({ top: 0, behavior: 'smooth' })
      }
    }

    function goToClinicStartTimeDiv() {
      const refs = clinicStartTimeRef.current // Get the stored refs
      const currentTime = dayjs() // Get the current time

      if (!refs || Object.keys(refs).length === 0) {
        toast.info('No clinic start times available.')
        return
      }

      // Convert stored time strings to dayjs objects and find the closest one
      const closestTime = Object.keys(refs)
        .map((time) => ({
          time,
          diff: Math.abs(dayjs(time, 'h:mm A').diff(currentTime, 'minute')),
        }))
        .sort((a, b) => a.diff - b.diff)[0]?.time // Get the smallest difference

      if (closestTime) {
        scrollToSlot(refs[closestTime])
      } else {
        toast.info('Could not find the clinic start time.')
      }
    }

    function goToFirstBookingOfDay() {
      const refs = bookedEventsRef.current // Get stored booked slots

      if (!refs || refs.size === 0) {
        // If no bookings, go to current time line
        scrollToPresentLine()
        return
      }

      // Find the earliest booking based on startTime
      const earliestSlot = [...refs.entries()]
        .map(([slot, ref]) => ({
          slot,
          dateTime: dayjs.utc(slot.startTime).local(), // Ensure startTime is used
          ref,
        }))
        .sort((a, b) => a.dateTime.diff(b.dateTime))[0]?.ref // Get the earliest slot ref

      if (earliestSlot) {
        scrollToSlot(earliestSlot) // Scroll to the first booking
      } else {
        // Fallback to current time line
        scrollToPresentLine()
      }
    }
    // const firstSlot = bookedEventsRef.current?.[0]
    // if (firstSlot) {
    //   scrollToSlot(firstSlot?.ref)
    // }
    function goToNextBooking() {
      const refs = bookedEventsRef.current // Get stored booked slots

      if (!refs || refs.size === 0) {
        // If no bookings, go to first booking (which will fallback to current time line)
        goToFirstBookingOfDay()
        return
      }

      // Convert entries to an array of objects
      const sortedBookings = [...refs.entries()]
        .map(([bookedSlot, ref]) => ({
          bookedSlot,
          dateTime: dayjs.utc(bookedSlot.startTime).local(),
          ref,
        }))
        .sort((a, b) => a.dateTime.diff(b.dateTime)) // Sort by time

      // Check if there's a booking currently in focus
      const hasFocusedBooking = clickedBookedSlotData && (clickedBookedSlotData.id || clickedBookedSlotData.startTime)
      
      if (hasFocusedBooking) {
        // Find the currently focused booking in the sorted list
        const currentBookingIndex = sortedBookings.findIndex(
          ({ bookedSlot }) => 
            bookedSlot.id === clickedBookedSlotData.id || 
            (bookedSlot.startTime === clickedBookedSlotData.startTime && bookedSlot.id === clickedBookedSlotData.id)
        )

        if (currentBookingIndex !== -1 && currentBookingIndex < sortedBookings.length - 1) {
          // Found the current booking, go to the next one after it (regardless of completion status)
          const nextBooking = sortedBookings[currentBookingIndex + 1]
          if (nextBooking) {
            scrollToSlot(nextBooking.ref)
            return
          }
        }
        // If current booking is the last one or not found, fall through to first incomplete
      }

      // If nothing is in focus, find the first incomplete booking
      const firstIncompleteBooking = sortedBookings.find(
        ({ bookedSlot }) => bookedSlot.markedCompleted === false,
      )

      if (firstIncompleteBooking) {
        // Found an incomplete booking, scroll to it
        scrollToSlot(firstIncompleteBooking.ref)
      } else {
        // No incomplete bookings found, go to first booking (same as goToFirstBookingOfDay)
        goToFirstBookingOfDay()
      }
    }

    async function getImportedFromItems() {
      const importedFromApiSignature =
        typeof window !== 'undefined' &&
        (window.location.pathname.startsWith('/support-home/') ||
          window.location.pathname.startsWith('/support/'))
          ? '/api/secure/cc/support/reference/imported-from-ids'
          : '/api/health/imported-from-ids'

      const resp = await getCPDataWithParams(importedFromApiSignature)
      if (resp.status === 200) {
        setImportedFromItems(resp?.data)
      }
    }

    // Delay scroll to current time by ~1 second after page load
    // This allows page to load normally with headers at top first
    // Then scroll to current time, which will hide headers during scroll
    // Headers will reappear when scrolling completes
    // Only scroll when viewing today's date
    useEffect(() => {
      const isToday = dayjs().format('DD-MM-YYYY') === selectedDate
      
      // Only scroll to current time if we're viewing today's date
      if (!isToday) {
        return
      }
      
      // Wait 1 second after page load before scrolling to current time
      // This ensures headers load at top first, then scroll happens
      const scrollTimeout = setTimeout(() => {
        scrollToPresentLine()
      }, 1000) // 1 second delay
      
      return () => {
        clearTimeout(scrollTimeout)
      }
    }, [mentorId, selectedDate])

    useEffect(() => {
      if (
        selectedClinicBrandData &&
        selectedClinicBrandData?.clinicBrand?.id === selectedClinic?.id
      ) {
      } else {
        getAndSetClinicBrandData(selectedClinic?.clinicBrandId)
      }
    }, [selectedClinic])

    let bookedSlotActionText
    if (
      clickedBookedSlotData?.discountAction &&
      clickedBookedSlotData?.discountAction?.discountType === 'discountedPrice' &&
      clickedBookedSlotData?.discountAction?.discountedPrice
    ) {
      bookedSlotActionText = `Updated Fee: ${clickedBookedSlotData?.discountAction?.discountedPrice}  `
    } else {
      bookedSlotActionText = ''
    }

    async function handleNewTherapyRequest(therapyBookingDataForThisEvent, bookedSlot) {
      setTherapyBookingDialogOpen(true)
      setDataForTherapyBookingDialog({ therapyBookingDataForThisEvent, bookedSlot })
    }

    function goToEvent(bookedEventToGoTo) {
      if (bookedSlotClickFunctionOverride) {
        bookedSlotClickFunctionOverride(bookedEventToGoTo)
        return
      }
      navigate(`/l/${bookedEventToGoTo?.slug}`, {
        // Preserve the query string (e.g. ?tab=appointments) so returning from the
        // live-session page lands back on the same tab the user navigated from.
        state: { from: `${window.location.pathname}${window.location.search}` },
      })
    }

    const useClinicAppointmentTypesFlag =
      useClinicAppointmentTypes && !Boolean(sessionStorage.getItem('useLegacyEventTypesForDayCal'))

    function newBookingDialogRender({ isInDialog = false, selectedPatient = null } = {}) {
      // Use pre-selected patient if available, otherwise use passed selectedPatient
      const patientToUse = preSelectedPatientForBooking || selectedPatient
      
      return (
        <NewBookingDialogComponent
          dialogTitle={
            <Box sx={{ display: 'flex' }}>
              <h5 style={{ fontWeight: '500' }}>{`Booking with ${mentorProfileData?.name}`}</h5>
            </Box>
          }
          useClinicServices={useClinicServices}
          dialogSecondaryTitle={<></>}
          open={newBookingDialogOpen}
          handleCloseDialog={() => {
            setNewBookingDialogOpen(false)
            setPreSelectedPatientForBooking(null) // Clear pre-selected patient when closing
          }}
          setNewBookingDialogOpen={setNewBookingDialogOpen}
          clinicId={selectedClinic?.id}
          clinicBrandId={selectedClinic?.clinicBrandId || ''}
          clinicData={thisClinicData}
          clinicBrandData={selectedClinicBrandData}
          bookedSlotsInDay={bookedSlotsInDay}
          updateBookingsInCalendar={getAndSetBookedSlotsOfSelectedDate}
          forMentor={forMentor}
          mentorProfileData={mentorProfileData}
          mentorAuthorisedToSaveAndGetPatientsUsingIdb={
            mentorAuthorisedToSaveAndGetPatientsUsingIdb
          }
          chambersData={chambersData}
          assistantProfileInThisClinic={assistantProfileInThisClinic}
          selectedEventTypeId={selectedEventTypeId}
          eventDataForSelectedEventType={eventDataForSelectedEventType}
          planDataForSelectedEventId={planDataForSelectedEventId}
          // eventTypesVisible={eventTypesListOverride || eventTypesVisible}
          eventTypesVisible={eventTypesVisible}
          setSelectedEventTypeId={setSelectedEventTypeId}
          clickedWantedSlotData={clickedWantedSlotData}
          setClickedWantedSlotData={setClickedWantedSlotData}
          setEventDataForReceiptPrinting={setEventDataForReceiptPrinting}
          setEventIdForReceiptPrinting={setEventIdForReceiptPrinting}
          openReceiptDialog={openReceiptDialog}
          allProfessionalsDataForAssistant={allProfessionalsDataForAssistant}
          selectedVisitEventType={selectedVisitEventType}
          vitalsFormGetApiSignature={vitalsFormGetApiSignature}
          setSelectedVisitEventType={setSelectedVisitEventType}
          meetingBookApiSignature={meetingBookApiSignature}
          patientSearchApiSignature={patientSearchApiSignature}
          pastPatientBookingsApiSignature={pastPatientBookingsApiSignature}
          createPatientApiSignature={createPatientApiSignature}
          paymentLinkToSendViaQRCode={paymentLinkToSendViaQRCode}
          isInDialog={isInDialog}
          selectedPatientOverride={patientToUse}
          forceAnonymousPatient={thisClinicData?.assistantsCanCreatePatientsWithoutContactDetails}
          importedFromItems={importedFromItems}
          forceHoldingSlot={forceHoldingSlot}
          hideBookButton={hideBookButton}
        />
      )
    }

    // Renders the "Search or Create Patient" booking dialog. Rendered once outside
    // the per-tab markup so it is available on both the Calendar tab (empty-slot
    // click) and the Appointments tab (the "New" button).
    function renderNewBookingDialog() {
      return newBookingDialogOpen && !useClinicServices ? (
        newBookingDialogRender()
      ) : (
        <Dialog
          open={!!newBookingDialogOpen}
          onClose={() => {
            setNewBookingDialogOpen(false)
            setPreSelectedPatientForBooking(null) // Clear pre-selected patient when closing
          }}
          PaperComponent={PastTherapyReportDialogPaper}
        >
          <NewBookingPopupWithPatientSelection
            clinicBrandId={selectedClinic?.clinicBrandId}
            clinicId={selectedClinic?.id}
            searchPatientApiSignature={patientSearchApiSignature}
            mentorProfileData={mentorProfileData}
            refreshData={getAndSetBookedSlotsOfSelectedDate}
            mentorsList={mentorsList}
            appointmentTypesList={clinicAppointmentTypesList}
            closeDialog={() => {
              setNewBookingDialogOpen(false)
              setPreSelectedPatientForBooking(null) // Clear pre-selected patient when closing
            }}
            selectedSlotData={clickedWantedSlotData}
            oldBookingDialogComponentRenderer={(props) => {
              return newBookingDialogRender(props)
            }}
            createPatientApiSignature={createPatientApiSignature}
            initialSelectedPatient={preSelectedPatientForBooking}
          />
        </Dialog>
      )
    }

    // Patient / vitals / booking dialogs that the Appointments-tab row actions
    // (More Options menu + the Vitals column) trigger via clickedBookedSlotData.
    // The Calendar-tab block renders its own copies; the tabs are mutually
    // exclusive so only one instance is ever mounted at a time.
    function renderClickedSlotDialogs() {
      return (
        <>
          <EditPatientDialogComponent
            dialogTitle="Patient Details"
            open={editPatientDialogOpen}
            handleCloseDialog={() => setEditPatientDialogOpen(false)}
            patientId={clickedBookedSlotData?.patientId}
            clinicId={selectedClinic?.id}
            clinicBrandId={selectedClinic?.clinicBrandId || ''}
            mentorProfileData={mentorProfileData}
            clinicBrandData={selectedClinicBrandData}
            importedFromItems={importedFromItems}
            patientSearchApiSignature={patientSearchApiSignature}
            pastPatientBookingsApiSignature={pastPatientBookingsApiSignature}
            patientUpdateApiSignature={patientUpdateApiSignature}
            patientTagsApiSignature={patientTagsApiSignature}
          />
          {addVitalsDialogOpen && (
            <VitalsComponentEntryDialog
              patientId={clickedBookedSlotData?.patientId}
              clinicId={selectedClinic?.id}
              bookedEventId={clickedBookedSlotData?.id}
              existingVitalData={(() => {
                const slot = clickedBookedSlotData
                if (!slot) return null
                const vital =
                  slot.vital ??
                  (Array.isArray(slot.vitals) && slot.vitals.length > 0 ? slot.vitals[0] : null)
                return vital && typeof vital === 'object' && Object.keys(vital).length > 0
                  ? vital
                  : null
              })()}
              vitalsFormGetApiSignature={(() => {
                if (!vitalsFormGetApiSignature) return undefined
                if (vitalsFormGetApiSignature.includes('/practitioner/vitals/form')) {
                  return vitalsFormGetApiSignature
                }
                if (vitalsFormGetApiSignature.includes('/clinic-assistant/vitals/form')) {
                  return `${vitalsFormGetApiSignature}?practitionerId=${mentorProfileData?.id}&clinicLocationId=${selectedClinic?.id}`
                }
                return vitalsFormGetApiSignature
              })()}
              closeFunction={() => setAddVitalsDialogOpen(false)}
              callbackFunctionAfterSave={async () => {
                setAddVitalsDialogOpen(false)
                await getAndSetBookedSlotsOfSelectedDate()
              }}
            />
          )}
          {editBookingDialogOpen && (
            <EditBookingDialogComponent
              open={editBookingDialogOpen}
              forMentor={forMentor}
              bookedEvent={clickedBookedSlotData}
              closeFunction={() => setEditBookingDialogOpen(false)}
              callbackFunctionAfterSave={() => {
                setEditBookingDialogOpen(false)
                getAndSetBookedSlotsOfSelectedDate()
              }}
              cancelAppointmentApiSignature={cancelAppointmentApiSignature}
              editBookingApiSignature={editBookingApiSignature}
              showRescheduleAction={isRescheduleVisibleForSlot(clickedBookedSlotData)}
              onRescheduleBooking={() => {
                setEditBookingDialogOpen(false)
                openStandaloneReschedule(clickedBookedSlotData)
              }}
            />
          )}
        </>
      )
    }

    return (
      <>
        {forMentor && getAllRolesInBoolean().isDoctor && (
          <CheckedInTherapistsCard />
        )}
        {/* Clinic Assistant Idle Popup - show when on CA route and not for mentor */}
        {!forMentor && isClinicAssistantRoute && (
          <ClinicAssistantIdlePopup
            currentClinicLocationData={thisClinicData}
            assistantName={mentorProfileData?.name || assistantProfileInThisClinic?.name}
          />
        )}
        {forMentor && showValidateCheckInPopup && (
          <ValidateCheckInPopup currentClinicLocationData={selectedClinic} />
        )}
        {useClinicAppointmentTypesFlag && (
          <div style={{ fontSize: '12px', color: 'purple' }}>
            Using clinic appointment types (NEW!) (
            <span
              onClick={() => {
                sessionStorage.setItem('useLegacyEventTypesForDayCal', true)
                window.location.reload()
              }}
            >
              <u>Switch back to legacy event types</u>
            </span>
            )
          </div>
        )}
        <Box sx={{ display: 'flex', height: isViewingRosterTab ? '100%' : 'auto', marginTop: 0, paddingTop: 0, margin: 0 }}>
          {isViewingCalendarTab && (() => {
            const isMobile = window.innerWidth < 768
            const tooltipPlacement = isMobile ? 'top' : 'left'
            return (
              <div className={activeSliderSlotId ? 'dimmed-content' : ''} style={{ marginTop: 0, paddingTop: 0, margin: 0 }}>
                <EDVCCalendarNavigator useSticky={stickySideButtons}>
                  <Tooltip 
                  title="Go to First Booking" 
                  placement={tooltipPlacement} 
                  arrow
                  enterDelay={300}
                  leaveDelay={100}
                >
                  <EDVCNavButton
                    onClick={goToFirstBookingOfDay}
                    aria-label="Go to first booking"
                    variant="first"
                  >
                    <FirstPage sx={{ 
                      fontSize: { xs: '16px', sm: '18px', md: '20px' }
                    }} />
                  </EDVCNavButton>
                </Tooltip>
                <Tooltip 
                  title="Go to Current Time" 
                  placement={tooltipPlacement} 
                  arrow
                  enterDelay={300}
                  leaveDelay={100}
                >
                  <EDVCNavButton
                    onClick={scrollToPresentLine}
                    aria-label="Go to current time"
                    variant="now"
                  >
                    <AccessTime sx={{ 
                      fontSize: { xs: '16px', sm: '18px', md: '20px' }
                    }} />
                  </EDVCNavButton>
                </Tooltip>
                <Tooltip 
                  title="Go to Next Booking" 
                  placement={tooltipPlacement} 
                  arrow
                  enterDelay={300}
                  leaveDelay={100}
                >
                  <EDVCNavButton
                    onClick={goToNextBooking}
                    aria-label="Go to next booking"
                    variant="next"
                  >
                    <SkipNext sx={{ 
                      fontSize: { xs: '16px', sm: '18px', md: '20px' }
                    }} />
                  </EDVCNavButton>
                  </Tooltip>
                </EDVCCalendarNavigator>
              </div>
            )
          })()}
          <EDVCParent 
            className={`w-100 ${compactUI ? 'p-0' : ''}`} 
            style={{ 
              marginTop: '8px', 
              paddingTop: 0 
            }}
          >
            {/* {isViewingCalendarRelatedTab && ( */}
            {!isViewingRosterTab && (
              <div className={activeSliderSlotId ? 'dimmed-content' : ''} style={{ marginTop: 0, paddingTop: 0, position: 'relative' }}>
                {/* Floating bar component - shows first line (picker + action buttons) when toggle is enabled */}
                {/* Only render CalendarHeaderFloatingBar if showCalendarHeaderAfterScroll is true to avoid duplicate buttons */}
                {showCalendarHeaderAfterScroll &&
                  !(
                    forMentor &&
                    hideFloatingHeaderDatePicker &&
                    hideFloatingHeaderFilterPills &&
                    hideFloatingHeaderOverflowMenu
                  ) && (
                <CalendarHeaderFloatingBar
                  selectedDate={selectedDate}
                  setSelectedDate={setSelectedDate}
                  onPrevClick={() => {
                    // Do not block navigating to yesterday when viewing today; see goToPreviousDate.
                    goToPreviousDate(selectedDate, setSelectedDate)
                  }}
                  onNextClick={() => {
                    setSelectedDate(dayjs(selectedDate, 'DD-MM-YYYY').add(1, 'day').format('DD-MM-YYYY'))
                  }}
                  handleGoToToday={() => {
                    setSelectedDate(dayjs().format('DD-MM-YYYY'))
                  }}
                  isToday={dayjs().format('DD-MM-YYYY') === selectedDate}
                  onOpenBookedSlotsDrawer={
                    hideFloatingHeaderBookedSlotsButton ? undefined : () => setBookedSlotsDrawerOpen(true)
                  }
                  onOpenPatientSearch={onOpenPatientSearch}
                  onOpenNewBooking={openNewBookingDialog}
                  showNewBookingButton={isViewingConsultsTab && !hideFloatingHeaderNewBooking}
                  onOpenPractitionerDrawer={onOpenPractitionerDrawer}
                  practitionerButtonProps={practitionerButtonProps}
                  getAndSetBookedSlotsOfSelectedDate={getAndSetBookedSlotsOfSelectedDate}
                  bookedSlotsInDayLoading={bookedSlotsInDayLoading}
                  hideRefreshButton={hideRefreshButton}
                  clinicSchedules={selectedClinic?.schedules}
                  mentorProfileData={mentorProfileData}
                  showPractitionerInfo={window.innerWidth >= 1200}
                  currentTime={dayjs().format('hh:mm A')}
                  currentClinic={selectedClinic}
                  forMentor={forMentor}
                  leftOffset={floatingHeaderLeftOffset}
                  hideClinicDetails={hideFloatingHeaderClinicDetails}
                  hideDatePicker={hideFloatingHeaderDatePicker}
                  hideOverflowMenu={hideFloatingHeaderOverflowMenu}
                  hideFilterPills={hideFloatingHeaderFilterPills}
                  eachSlotDurationInMins={eachSlotDurationInMins}
                  setEachSlotDurationInMins={isViewingCalendarTab ? (newValue) => {
                    userManuallySetSlotDurationRef.current = true
                    setIsSlotDurationManuallySet(true)
                    setEachSlotDurationInMins(newValue)
                  } : undefined}
                  initialSlotDuration={initialSlotDuration}
                  includeReportRelatedData={includeReportRelatedData}
                  setIncludeReportRelatedData={setIncludeReportRelatedData}
                  hideToggleSwitchForReportRelatedData={hideToggleSwitchForReportRelatedData}
                  showCalendarHeaderAfterScroll={showCalendarHeaderAfterScroll}
                  setShowCalendarHeaderAfterScroll={setShowCalendarHeaderAfterScroll}
                  selectedFilterType={selectedFilterType}
                  setSelectedFilterType={setSelectedFilterType}
                  bookedSlotsInDay={bookedSlotsInDay}
                  cancelledCount={cancelledCount}
                  showCancelledOnly={showCancelledOnly}
                  setShowCancelledOnly={setShowCancelledOnly}
                  showCancelledChip={showCancelledChipInHeader}
                />
                )}
                <DayViewCalendarFloatingHeader
                LeftSideExtraComponent={LeftSideExtraComponent}
                RightSideExtraComponent={RightSideExtraComponent}
                bookedSlotsInDay={bookedSlotsInDay}
                forMentor={forMentor}
                hideDatePicker={hideFloatingHeaderDatePicker}
                hideOverflowMenu={hideFloatingHeaderOverflowMenu}
                hideFilterPills={hideFloatingHeaderFilterPills}
                selectedDate={selectedDate}
                mentorProfileData={mentorProfileData}
                bookedSlotsInDayLoading={bookedSlotsInDayLoading}
                getAndSetBookedSlotsOfSelectedDate={getAndSetBookedSlotsOfSelectedDate}
                isViewingCalendarRelatedTab={isViewingCalendarRelatedTab}
                scrollToPresentLine={scrollToPresentLine}
                selectedDateUtcStartTime={selectedDateUtcStartTime}
                selectedDateUtcEndTime={selectedDateUtcEndTime}
                setSelectedDate={setSelectedDate}
                headerContainerStyles={headerContainerStyles}
                compactUI={compactUI}
                hideRefreshButton={hideRefreshButton}
                mentorsList={mentorsList}
                onChangeMentorProfileData={onChangeMentorProfileData}
                cancelledCount={cancelledCount}
                setShowCancelledOnly={setShowCancelledOnly}
                showCancelledOnly={showCancelledOnly}
                showCancelledChip={showCancelledChipInHeader}
                showBookingPopupButton={isViewingConsultsTab}
                hideDropdownForMentorSelection={hideDropdownForMentorSelection}
                includeReportRelatedData={includeReportRelatedData}
                setIncludeReportRelatedData={setIncludeReportRelatedData}
                hideToggleSwitchForReportRelatedData={hideToggleSwitchForReportRelatedData}
                onOpenBookedSlotsDrawer={
                  hideFloatingHeaderBookedSlotsButton ? undefined : () => setBookedSlotsDrawerOpen(true)
                }
                onOpenPatientSearch={onOpenPatientSearch}
                onOpenNewBooking={openNewBookingDialog}
                showNewBookingButton={isViewingConsultsTab && !hideFloatingHeaderNewBooking}
                onOpenPractitionerDrawer={onOpenPractitionerDrawer}
                practitionerButtonProps={practitionerButtonProps}
                selectedFilterType={selectedFilterType}
                setSelectedFilterType={setSelectedFilterType}
                clinicSchedules={thisClinicData?.schedules}
                eachSlotDurationInMins={eachSlotDurationInMins}
                setEachSlotDurationInMins={isViewingCalendarTab ? (newValue) => {
                  userManuallySetSlotDurationRef.current = true
                  setIsSlotDurationManuallySet(true)
                  setEachSlotDurationInMins(newValue)
                } : undefined}
                initialSlotDuration={Math.max(initialSlotDuration || 15, 15)}
                thisClinicData={thisClinicData}
                roomId={roomId}
                chambersData={chambersData}
                resetSlotDurationToSystem={resetSlotDurationToSystem}
                isSlotDurationManuallySet={isSlotDurationManuallySet}
                isFloatingBarVisible={isFloatingBarActuallyVisible}
              />
              </div>
            )}
            {isViewingRosterTab && (
              <MyCurrentClinicRoster
                roomId={roomId}
                clinicBrandId={selectedClinic?.clinicBrandId}
                clinicLocationId={selectedClinic?.id}
                mentorProfile={mentorProfileData}
                clinicLocationSchedule={selectedClinic?.schedules}
              />
            )}
            {/* )} */}
            {isViewingCalendarTab && (
              <div style={{ maxWidth: '968px', margin: '0 auto', width: '100%' }}>
                <Popover
                  id="bookedSlotPopover"
                  anchorEl={clickedBookedSlotAnchor}
                  open={Boolean(clickedBookedSlotAnchor)}
                  onClose={() => setClickedBookedSlotAnchor(null)}
                  anchorOrigin={{
                    vertical: 'center',
                    horizontal: 'center',
                  }}
                  transformOrigin={{
                    vertical: 'top',
                    horizontal: 'center',
                  }}
                  PaperProps={{
                    onClick: (e) => {
                      // Prevent popover from closing when clicking inside it
                      e.stopPropagation()
                    },
                  }}
                >
                  <SlotPopupSummary
                    clickedBookedSlotData={clickedBookedSlotData}
                    setClickedBookedSlotAnchor={setClickedBookedSlotAnchor}
                    getAndSetBookedSlotsOfSelectedDate={getAndSetBookedSlotsOfSelectedDate}
                    markBookedSessionAsCompletedFunction={markBookedSessionAsCompletedFunction}
                    platformOptions={platformOptions}
                    bookedSlotActionText={bookedSlotActionText}
                    pendingActionsOfPerson={pendingActionsOfPerson}
                    forMentor={forMentor}
                    hideStartButton={hideStartButton}
                    hideMarkedCompletedButton={hideMarkedCompletedButton}
                    goToEvent={goToEvent}
                    setCancelDialogData={setCancelDialogData}
                    setMarkCompletedDialogData={setMarkCompletedDialogData}
                    onOpenMainDrawer={(bookedSlot) => {
                      setSelectedBookedSlotForDrawer(bookedSlot)
                      setBookedEventDetailsDrawerOpen(true)
                      setClickedBookedSlotAnchor(null)
                    }}
                  />
                </Popover>
                <Dialog
                  open={openReceiptPrintingDialog}
                  PaperComponent={ReceiptPrintDialogPaper}
                  onClose={closeReceiptDialog}
                  className="receipt-print-dialog"
                >
                  <Suspense fallback={<Box sx={{ p: 3, display: 'flex', justifyContent: 'center' }}><CircularProgress /></Box>}>
                    <ReceiptPrintingComponent
                      bookedEvent={dataForReceiptPrinting.eventData}
                      bookedEventId={dataForReceiptPrinting.eventId}
                      patientSearchApiSignature={patientSearchApiSignature}
                      closeDialog={closeReceiptDialog}
                    />
                  </Suspense>
                </Dialog>

                {/* Show loading skeleton while fetching booked slots */}
                {bookedSlotsInDayLoading && bookedSlotsInDay.length === 0 ? (
                  <Box
                    sx={{
                      display: 'flex',
                      flexDirection: 'column',
                      gap: 2,
                      padding: 3,
                      alignItems: 'center',
                      justifyContent: 'center',
                      minHeight: '400px',
                    }}
                  >
                    <CircularProgress size={40} sx={{ mb: 2 }} />
                    <Box sx={{ width: '100%', maxWidth: '600px' }}>
                      <Skeleton variant="rectangular" height={60} sx={{ mb: 1, borderRadius: 1 }} />
                      <Skeleton variant="rectangular" height={60} sx={{ mb: 1, borderRadius: 1 }} />
                      <Skeleton variant="rectangular" height={60} sx={{ mb: 1, borderRadius: 1 }} />
                      <Skeleton variant="rectangular" height={60} sx={{ mb: 1, borderRadius: 1 }} />
                      <Skeleton variant="rectangular" height={60} sx={{ borderRadius: 1 }} />
                    </Box>
                  </Box>
                ) : (
                  <>
                    {/* Render three time sections: Early Morning, Work Day, Late Night */}
                    {renderTimeSection('earlyMorning', splitSlotsIntoTimeSections.earlyMorning, 0)}
                    {renderTimeSection('workDay', splitSlotsIntoTimeSections.workDay, 1)}
                    {renderTimeSection('lateNight', splitSlotsIntoTimeSections.lateNight, 2)}
                  </>
                )}
                
                {/* Legacy code - keeping for reference but not used */}
                {false && slotGroups?.map((timeBlock, timeBlockIndex) => {
                  // Debug logging
                  if (timeBlock && timeBlock.length > 0) {
                    const firstSlotTime = dayjs(timeBlock[0]?.startTime)
                    const hour = firstSlotTime.hour()
                    const lastSlotTime = dayjs(timeBlock[timeBlock.length - 1]?.endTime)
                    const lastHour = lastSlotTime.hour()
                    
                    // Log early morning and late night blocks
                    if (hour >= 0 && hour < 7) {
                      console.log('Early morning block found:', {
                        index: timeBlockIndex,
                        firstSlot: timeBlock[0]?.startTime,
                        hour,
                        lastHour,
                        slotCount: timeBlock.length,
                      })
                    }
                    if (lastHour >= 22 || hour >= 22) {
                      console.log('Late night block found:', {
                        index: timeBlockIndex,
                        firstSlot: timeBlock[0]?.startTime,
                        hour,
                        lastHour,
                        slotCount: timeBlock.length,
                      })
                    }
                  }
                  
                  const shouldCollapse = shouldCollapseTimeBlock(timeBlock)
                  const hasBookings = hasBookingsInTimeBlock(timeBlock)
                  const isCollapsed = isTimeBlockCollapsed(timeBlock)
                  const shouldShowCollapse = shouldCollapse && !hasBookings
                  
                  // Debug the collapse decision
                  if (shouldCollapse) {
                    console.log('Collapse decision:', {
                      index: timeBlockIndex,
                      shouldCollapse,
                      hasBookings,
                      isCollapsed,
                      shouldShowCollapse,
                      collapsedRanges,
                    })
                  }
                  
                  return (
                    <>
                      {shouldShowCollapse && isCollapsed ? (
                        <EDVCCollapsedHeader
                          key={`collapsed-${timeBlockIndex}`}
                          onClick={() => handleToggleCollapse(timeBlock)}
                          className={isCollapsed ? '' : 'expanded'}
                          role="button"
                          aria-expanded={!isCollapsed}
                          aria-label={getCollapseLabel(timeBlock)}
                          tabIndex={0}
                          onKeyDown={(e) => {
                            if (e.key === 'Enter' || e.key === ' ') {
                              e.preventDefault()
                              handleToggleCollapse(timeBlock)
                            }
                          }}
                        >
                          <span className="collapse-label">
                            {getCollapseLabel(timeBlock)}
                          </span>
                          {isCollapsed ? (
                            <ExpandMore className="collapse-icon" />
                          ) : (
                            <ExpandLess className="collapse-icon" />
                          )}
                        </EDVCCollapsedHeader>
                      ) : (
                        <EDVCContainer 
                          key={timeBlockIndex}
                          className={activeSliderSlotId ? 'dimmed-content' : ''}
                        >
                        {/* available slots */}
                        {timeBlock?.map((slot, slotIndex) => (
                          <EDVCSlotItemContainer
                            key={slotIndex}
                            heightOfEachSlot={heightOfEachSlot}
                          >
                            <EDVCSlotItemTime>
                              <EDVCSlotStartTime style={{ top: '0px' }}>
                                {dayjs(slot?.startTime)?.format('hh:mmA')}
                              </EDVCSlotStartTime>
                            </EDVCSlotItemTime>
                            <EDVCSlotItem
                              heightOfEachSlot={heightOfEachSlot}
                              onClick={() => {
                                if (emptySlotClickFunctionOverride) {
                                  emptySlotClickFunctionOverride()
                                } else {
                                  setClickedWantedSlotData({
                                    // endTime: dayjs(slot?.startTime)
                                    //   .add(1, 'minute')
                                    //   .format('YYYY-MM-DDTHH:mm:ssZ'),
                                    endTime: slot?.endTime,
                                    startTime: slot?.startTime,
                                  })
                                  setNewBookingDialogOpen(true)
                                }
                              }}
                            ></EDVCSlotItem>
                          </EDVCSlotItemContainer>
                        ))}

                        {/* highlighted slots */}
                        {highlightedForAvailabilitySlotGroups?.map(
                          (hightlightedBlock, hightlightedBlockIndex) => {
                            const startTimeOfTimeBlock = dayjs(timeBlock[0]?.startTime)
                            const endTimeOfTimeBlock = dayjs(
                              timeBlock[timeBlock?.length - 1]?.endTime,
                            )
                            if (
                              dayjs(hightlightedBlock?.startTime).isBetween(
                                startTimeOfTimeBlock,
                                endTimeOfTimeBlock,
                                null,
                                '[]',
                              )
                            ) {
                              const durationOfHighlightedSlotInMins = dayjs(
                                hightlightedBlock?.endTime,
                              )?.diff(dayjs(hightlightedBlock?.startTime), 'minutes')
                              const minutesPassedSinceStartOfTimeBlockTillThisHighlight = dayjs(
                                hightlightedBlock?.startTime,
                              )?.diff(startTimeOfTimeBlock, 'minutes')
                              return (
                                <HighlightedBlockContainer
                                  style={{ background: 'cream' }}
                                  key={hightlightedBlockIndex}
                                  fromTopInPx={
                                    (minutesPassedSinceStartOfTimeBlockTillThisHighlight /
                                      eachSlotDurationInMins) *
                                    heightOfEachSlot
                                  }
                                  heightInPx={
                                    (durationOfHighlightedSlotInMins / eachSlotDurationInMins) *
                                    heightOfEachSlot
                                  }
                                >
                                  <HighlightedBlockStartMarker
                                    ref={(e) => {
                                      if (e) {
                                        clinicStartTimeRef.current[
                                          dayjs(hightlightedBlock?.startTime)?.format('h:mm A')
                                        ] = e
                                      }
                                    }}
                                  >
                                    CLINIC OPENS{' - '}
                                    {dayjs(hightlightedBlock?.startTime).format('h:mm A')}
                                  </HighlightedBlockStartMarker>
                                  <HighlightedBlockEndMarker>
                                    CLINIC CLOSES -{' '}
                                    {dayjs(hightlightedBlock?.endTime).format('h:mm A')}
                                  </HighlightedBlockEndMarker>
                                </HighlightedBlockContainer>
                              )
                            } else {
                              return null
                            }
                          },
                        )}

                        {/* booked slots overlay colored - */}
                        {(() => {
                          const startTimeOfTimeBlock = dayjs(timeBlock[0]?.startTime)
                          const endTimeOfTimeBlock = dayjs(
                            timeBlock[timeBlock?.length - 1]?.endTime,
                          )
                          const overlapMapForBlock = getOverlapGroupsForTimeBlock(
                            bookedSlotsInDay,
                            startTimeOfTimeBlock,
                            endTimeOfTimeBlock,
                          )
                          return bookedSlotsInDay?.map((bookedSlot, bookedSlotIndex) => {
                            const slotStart = dayjs(bookedSlot?.startTimeForDayCal || bookedSlot?.startTime)
                            const slotEnd = dayjs(bookedSlot?.endTimeForDayCal || bookedSlot?.endTime)
                            
                            // Check if slot overlaps with this time block (including partial overlap)
                            const slotOverlapsBlock = slotStart.isValid() && slotEnd.isValid() &&
                              slotStart.isBefore(endTimeOfTimeBlock) && slotEnd.isAfter(startTimeOfTimeBlock)
                            
                            if (slotOverlapsBlock) {
                              const overlap = overlapMapForBlock.get(bookedSlot?.id) ?? overlapMapForBlock.get(bookedSlot?.uid) ?? { overlapIndex: 0, overlapTotal: 1 }
                              // Calculate the visible portion of the slot in this section
                              const visibleStart = slotStart.isAfter(startTimeOfTimeBlock) ? slotStart : startTimeOfTimeBlock
                              const visibleEnd = slotEnd.isBefore(endTimeOfTimeBlock) ? slotEnd : endTimeOfTimeBlock
                              
                              // Calculate duration and position for the visible portion
                              const visibleDurationInMins = visibleEnd.diff(visibleStart, 'minutes')
                              const minutesPassedSinceStartOfTimeBlock = visibleStart.diff(startTimeOfTimeBlock, 'minutes')
                              
                              // Use the visible duration instead of full duration
                              const durationOfBookedSlotInMins = visibleDurationInMins
                              const pendingActionsForThisBookedEvent = pendingActionsOfPerson?.filter(
                                (item) => item?.bookedEventId === bookedSlot?.id,
                              )
                              const therapyBookingDataForThisEvent =
                                pendingActionsForThisBookedEvent?.find(
                                  (pa) => pa?.type === 'schedule-therapy',
                                )
                              const slotBlocked = selectedClinic?.id !== bookedSlot?.clinicId
                              
                              return (
                                <>
                                <BookedSlotItemContent
                                  key={bookedSlotIndex}
                                  overlapIndex={overlap.overlapIndex}
                                  overlapTotal={overlap.overlapTotal}
                                patientHistoryGetApiSignature={patientHistoryGetApiSignature}
                                onOpenMainDrawer={(bookedSlot) => {
                                  setSelectedBookedSlotForDrawer(bookedSlot)
                                  setBookedEventDetailsDrawerOpen(true)
                                }}
                                onClickItem={(event) => {
                                  // Allow clicking for mentors and support portal even if from different clinic
                                  const isSupportPortal = typeof window !== 'undefined' && window.location?.pathname?.includes('/support')
                                  if (slotBlocked && !forMentor && !isSupportPortal) {
                                    return
                                  }
                                  // CRITICAL: For mentor portal, only allow opening session page for booked status
                                  // Events/Bookings which do not have status=booked cannot be opened (session page cannot be opened)
                                  // Only mentor portal has the session opening functionality
                                  const isBooked = bookedSlot?.status === STATUS_SESSION_BOOKED
                                  const isCompleted = bookedSlot?.status === STATUS_SESSION_COMPLETED
                                  const isBlockedForPayment = 
                                    bookedSlot?.status === BOOKING_STATUSES.BOOKING_INITIATED &&
                                    bookedSlot?.paymentStatus === BOOKING_PAYMENT_STATUSES.PENDING
                                  const isCancelled = bookedSlot?.status === BOOKING_SESSION_STATUS.CANCELLED
                                  
                                  // For mentor portal: Only allow booked status to open session (prevent non-booked from opening)
                                  // For support portal: Allow any non-cancelled slot to open drawer
                                  // For other portals: Allow booked/completed or blocked for payment
                                  const shouldOpenDrawer = 
                                    (isSupportPortal && !isCancelled) ||
                                    (forMentor ? isBooked : (isBooked || isCompleted)) ||
                                    (isBlockedForPayment && forMentor)
                                  
                                  if (!shouldOpenDrawer) {
                                    return // Don't open drawer/session page
                                  }
                                  
                                  if (bookedSlotClickFunctionOverride) {
                                    // For mentor portal, ensure only booked status can trigger session opening
                                    if (forMentor && !isBooked && !isBlockedForPayment) {
                                      return // Prevent non-booked events from opening session in mentor portal
                                    }
                                    bookedSlotClickFunctionOverride(bookedSlot)
                                  } else {
                                    setClickedBookedSlotData(bookedSlot)
                                    if (isSupportPortal) {
                                      // Open drawer for support portal
                                      setSelectedBookedSlotForDrawer(bookedSlot)
                                      setBookedEventDetailsDrawerOpen(true)
                                    } else {
                                      // Use popover for other portals
                                      // For mentor portal, only allow if booked (session opening)
                                      if (forMentor) {
                                        if (!isBooked && !isBlockedForPayment) {
                                          return // Prevent non-booked events from opening session in mentor portal
                                        }
                                        handleBookedSlotOpenPopover(event)
                                        if (fireCheckInApiOnCalendarItemClick) {
                                          checkIntoCurrentlyPeekedInRoomAsInPerson({
                                            currentClinic: selectedClinic,
                                          })
                                        }
                                      } else {
                                        handleBookedSlotOpenPopover(event)
                                      }
                                    }
                                  }
                                }}
                                showPatientHistoryInUploadPrescriptionWidget={
                                  showPatientHistoryInUploadPrescriptionWidget
                                }
                                mentorProfileData={mentorProfileData}
                                refreshBookedSlots={getAndSetBookedSlotsOfSelectedDate}
                                durationOfBookedSlotInMins={durationOfBookedSlotInMins}
                                eachSlotDurationInMins={eachSlotDurationInMins}
                                heightOfEachSlot={heightOfEachSlot}
                                showViewReportsButtonInActionButtons={
                                  showViewReportsButtonInActionButtons
                                }
                                openReportViewDrawer={(slot) => {
                                  setSelectedBookedSlotForReport(slot)
                                  setReportViewDrawerOpen(true)
                                }}
                                minutesPassedSinceStartOfTimeBlock={
                                  minutesPassedSinceStartOfTimeBlock
                                }
                                prescriptionGetApiSignature={getPrescriptionImagesApiSignature}
                                prescriptionSaveApiSignature={savePrescriptionImagesApiSignature}
                                bookedSlot={bookedSlot}
                                hidePatientContactDetails={hidePatientContactDetails}
                                canPersonPrintPrescription={canPersonPrintPrescription}
                                forMentor={forMentor}
                                handleNewTherapyRequest={handleNewTherapyRequest}
                                hidePreviousTherapyReportsButton={hidePreviousTherapyReportsButton}
                                minimalUI={minimalUI}
                                openReceiptDialog={openReceiptDialog}
                                pendingActionsForThisBookedEvent={pendingActionsForThisBookedEvent}
                                setAddVitalsDialogOpen={setAddVitalsDialogOpen}
                                setClickedBookedSlotData={setClickedBookedSlotData}
                                setEditBookingDialogOpen={setEditBookingDialogOpen}
                                setEditPatientDialogOpen={setEditPatientDialogOpen}
                                setEventDataForReceiptPrinting={setEventDataForReceiptPrinting}
                                setPendingActionsDialogOpen={setPendingActionsDialogOpen}
                                fileUploadApiSignature={fileUploadApiSignature}
                                showGalleryForPrescriptionImages={showGalleryForPrescriptionImages}
                                slotBlocked={slotBlocked}
                                sendPrescriptionPdfApiSignature={sendPrescriptionPdfApiSignature}
                                hideRescheduleDialog={hideRescheduleDialog}
                                canRescheduleBookedSlot={canRescheduleBookedSlot}
                                rescheduleDialogType={rescheduleDialogType}
                                forceShowJoiningDetailsActionButton={
                                  forceShowJoiningDetailsActionButton
                                }
                                prescriptionImagesGetApiSignature={
                                  getPrescriptionImagesApiSignature
                                }
                                prescriptionImagesSaveApiSignature={
                                  savePrescriptionImagesApiSignature
                                }
                                clinicBrandData={
                                  slotBlocked && (forMentor || window.location?.pathname?.includes('/support'))
                                    ? bookedSlot?.clinicBrandData
                                    : selectedClinicBrandData
                                }
                                showPrescriptionPdfDownloadButtons={shouldShowPrescriptionPdfDownloadButtons(
                                  {
                                    forMentor,
                                    bookedSlot,
                                    prescriptionViewSettingsData:
                                      prescrpitionViewSettingsStoreData?.data,
                                  },
                                )}
                                slotContainerRef={(e) => {
                                  if (e) {
                                    bookedEventsRef.current.set(bookedSlot, e) // Store slot-reference mapping
                                  } else {
                                    bookedEventsRef.current.delete(bookedSlot) // Clean up on unmount
                                  }
                                }}
                                therapyBookingDataForThisEvent={therapyBookingDataForThisEvent}
                                fireCheckInApiOnCalendarItemClick={
                                  fireCheckInApiOnCalendarItemClick
                                }
                                sendPublicSessionLinkToPatientSignature={
                                  sendPublicSessionLinkToPatientSignature
                                }
                                sendSecureSessionLinkToPatientSignature={
                                  sendSecureSessionLinkToPatientSignature
                                }
                                clinicLocationData={
                                  slotBlocked && (forMentor || window.location?.pathname?.includes('/support'))
                                    ? bookedSlot?.clinicLocation || bookedSlot?.clinicLocationData
                                    : selectedClinic
                                }
                                clinicAssociationsData={getClinicDataFromAssociations(bookedSlot?.clinicId, bookedSlot?.clinicBrandId)}
                                clinicAssociations={clinicAssociations}
                                includeReportRelatedData={includeReportRelatedData}
                                showSendingLinkButtonsToSupport={showSendingLinkButtonsToSupport}
                                bookedSlotsInDay={bookedSlotsInDay}
                                onGoToEvent={goToEvent}
                              />
                                </>
                              )
                            } else {
                              return null
                            }
                          })
                        })()}

                        {Boolean(newBookingDialogOpen) &&
                          clickedWantedSlotData?.startTime &&
                          clickedWantedSlotData?.endTime &&
                          (() => {
                            const isBetweenCondition = dayjs(
                              clickedWantedSlotData?.startTime,
                            ).isBetween(
                              dayjs(timeBlock[0]?.startTime),
                              dayjs(timeBlock[timeBlock?.length - 1]?.endTime),
                              null,
                              '[]',
                            )

                            const fromTopInPxValue = getHeightFromTopInSlotGroup(
                              timeBlock,
                              clickedWantedSlotData,
                              eachSlotDurationInMins,
                            )

                            if (isBetweenCondition) {
                              return (
                                <EDVCSlotColoredItem
                                  selected={true}
                                  booked={false}
                                  fromTopInPx={fromTopInPxValue}
                                  heightInPx={
                                    (dayjs(clickedWantedSlotData?.endTime)?.diff(
                                      dayjs(clickedWantedSlotData?.startTime),
                                      'minutes',
                                    ) /
                                      eachSlotDurationInMins) *
                                    heightOfEachSlot
                                  }
                                ></EDVCSlotColoredItem>
                              )
                            }
                          })()}

                        {(() => {
                          // Guard: Ensure timeBlock exists and has items
                          if (!timeBlock || timeBlock.length === 0 || !timeBlock[0]?.startTime) {
                            return null
                          }
                          
                          const startTimeOfTimeBlock = dayjs(timeBlock[0]?.startTime)
                          const endTimeOfTimeBlock = dayjs(timeBlock[timeBlock?.length - 1]?.endTime)
                          
                          // Guard: Ensure times are valid
                          if (!startTimeOfTimeBlock.isValid() || !endTimeOfTimeBlock.isValid()) {
                            return null
                          }
                          
                          if (
                            dayjs().isBetween(
                              startTimeOfTimeBlock,
                              endTimeOfTimeBlock,
                              null,
                              '[]',
                            )
                          ) {
                            // Calculate current time - use dayjs() directly as timeBlock times are already in the correct timezone
                            const currentTimeFormatted = dayjs().format('h:mm A')
                            const fromTopInPx = getHeightFromTopForCurrentTime(
                              timeBlock,
                              eachSlotDurationInMins,
                            )
                            
                            return (
                              <div className="time-line-wrapper" style={{ position: 'relative' }}>
                                <EDVCPresentTimeLine
                                  ref={presentTimeLineRef}
                                  fromTopInPx={fromTopInPx}
                                >
                                  <EDVCPresentTimeLineDot />
                                </EDVCPresentTimeLine>
                                <EDVCPresentTimeTag
                                  fromTopInPx={fromTopInPx}
                                  className="time-tag"
                                >
                                  {currentTimeFormatted}
                                </EDVCPresentTimeTag>
                              </div>
                            )
                          }
                          return null
                        })()}
                      </EDVCContainer>
                      )}
                      
                      {/* for the conditional container to show time now strip */}
                      {timeBlock && timeBlock.length > 0 && timeBlock[timeBlock?.length - 1]?.endTime &&
                       dayjs().isBetween(
                        dayjs(timeBlock[timeBlock?.length - 1]?.endTime, 'YYYY-MM-DDTHH:mm:ssZ'),
                        dayjs(
                          timeBlock[timeBlock?.length - 1]?.endTime,
                          'YYYY-MM-DDTHH:mm:ssZ',
                        ).endOf('day'),
                        null,
                        '[]',
                      ) &&
                        (() => {
                          if (timeBlockIndex === slotGroups?.length - 1) {
                            // Calculate current time in clinic timezone
                            const currentTimeInClinicTZ = dayjs(timeBlock[timeBlock?.length - 1]?.endTime).isValid()
                              ? dayjs(timeBlock[timeBlock?.length - 1]?.endTime)
                              : dayjs()
                            const currentTimeFormatted = currentTimeInClinicTZ.format('h:mm A')
                            
                            return (
                              <EDVCExtraSlotItemForTime>
                                <div className="time-line-wrapper" style={{ position: 'relative' }}>
                                  <EDVCPresentTimeLine ref={presentTimeLineRef} fromTopInPx={0}>
                                    <EDVCPresentTimeLineDot />
                                  </EDVCPresentTimeLine>
                                  <EDVCPresentTimeTag
                                    fromTopInPx={0}
                                    className="time-tag"
                                  >
                                    {currentTimeFormatted}
                                  </EDVCPresentTimeTag>
                                </div>
                                <EDVCSlotItemTime>
                                  <EDVCSlotStartTime style={{ top: 'unset', right: 'unset' }}>
                                    {dayjs()?.format('hh:mmA')}
                                  </EDVCSlotStartTime>
                                </EDVCSlotItemTime>
                              </EDVCExtraSlotItemForTime>
                            )
                          } else if (
                            dayjs().isBefore(
                              dayjs(
                                slotGroups?.[timeBlockIndex + 1]?.[0]?.startTime,
                                'YYYY-MM-DDTHH:mm:ssZ',
                              ),
                              null,
                              '[]',
                            )
                          ) {
                            return (
                              <EDVCExtraSlotItemForTime>
                                <EDVCPresentTimeLine ref={presentTimeLineRef} fromTopInPx={0}>
                                  <EDVCPresentTimeLineDot />
                                </EDVCPresentTimeLine>
                                <EDVCSlotItemTime>
                                  <EDVCSlotStartTime style={{ top: 'unset', right: 'unset' }}>
                                    {dayjs()?.format('hh:mmA')}
                                  </EDVCSlotStartTime>
                                </EDVCSlotItemTime>
                              </EDVCExtraSlotItemForTime>
                            )
                          }
                        })()}
                    </>
                  )
                })}
                {!selectedClinicBrandData?.bookingFormSettings?.hidePricingDetailsInBookingForm && (
                  <Suspense fallback={<Box sx={{ p: 2, display: 'flex', justifyContent: 'center' }}><CircularProgress size={24} /></Box>}>
                    <DayViewFeeComponent
                      bookedSlots={bookedSlotsInDay}
                      currentClinicData={thisClinicData}
                      clinicId={thisClinicData?.id}
                      selectedDate={selectedDate}
                      forMentor={forMentor}
                      mentorProfileData={mentorProfileData}
                    />
                  </Suspense>
                )}
                <EditPatientDialogComponent
                  dialogTitle="Patient Details"
                  open={editPatientDialogOpen}
                  handleCloseDialog={() => setEditPatientDialogOpen(false)}
                  patientId={clickedBookedSlotData?.patientId}
                  clinicId={selectedClinic?.id}
                  clinicBrandId={selectedClinic?.clinicBrandId || ''}
                  mentorProfileData={mentorProfileData}
                  clinicBrandData={selectedClinicBrandData}
                  importedFromItems={importedFromItems}
                  patientSearchApiSignature={patientSearchApiSignature}
                  pastPatientBookingsApiSignature={pastPatientBookingsApiSignature}
                  patientUpdateApiSignature={patientUpdateApiSignature}
                  patientTagsApiSignature={patientTagsApiSignature}
                />
                {addVitalsDialogOpen && (
                  <VitalsComponentEntryDialog
                    patientId={clickedBookedSlotData?.patientId}
                    clinicId={selectedClinic?.id}
                    bookedEventId={clickedBookedSlotData?.id}
                    existingVitalData={(() => {
                      const slot = clickedBookedSlotData
                      if (!slot) return null
                      const vital = slot.vital ?? (Array.isArray(slot.vitals) && slot.vitals.length > 0 ? slot.vitals[0] : null)
                      return vital && typeof vital === 'object' && Object.keys(vital).length > 0 ? vital : null
                    })()}
                    vitalsFormGetApiSignature={(() => {
                      if (!vitalsFormGetApiSignature) return undefined
                      
                      // If it's the practitioner API, use as-is (no params needed)
                      if (vitalsFormGetApiSignature.includes('/practitioner/vitals/form')) {
                        return vitalsFormGetApiSignature
                      }
                      
                      // For clinic-assistant API, add params
                      if (vitalsFormGetApiSignature.includes('/clinic-assistant/vitals/form')) {
                        return `${vitalsFormGetApiSignature}?practitionerId=${mentorProfileData?.id}&clinicLocationId=${selectedClinic?.id}`
                      }
                      
                      // Default: return as-is
                      return vitalsFormGetApiSignature
                    })()}
                    closeFunction={() => setAddVitalsDialogOpen(false)}
                    callbackFunctionAfterSave={async () => {
                      setAddVitalsDialogOpen(false)
                      // Refresh booked slots to update vitals display
                      await getAndSetBookedSlotsOfSelectedDate()
                    }}
                  />
                )}
                <PendingActionsDCDialogComponent
                  open={pendingActionsDialogOpen}
                  handleCloseDialog={() => setPendingActionsDialogOpen(false)}
                  thisBookedSlotData={clickedBookedSlotData}
                  pendingActionsOfThisPatient={pendingActionsOfPerson?.filter(
                    (item) => item?.patientId === clickedBookedSlotData?.patientId,
                  )}
                  refreshPendingActions={getAndSetBookedSlotsOfSelectedDate}
                  clinicId={selectedClinic?.id}
                  patientId={clickedBookedSlotData?.patientId}
                  scheduleForNextVisit={scheduleForSelectedEventType}
                  professionalDataForNextVisit={mentorProfileData}
                  eventDataForNextVisit={eventDataForSelectedEventType}
                  allProfessionalsDataForAssistant={allProfessionalsDataForAssistant}
                  selectedClinic={selectedClinic}
                />
                {editBookingDialogOpen && (
                  <EditBookingDialogComponent
                    open={editBookingDialogOpen}
                    forMentor={forMentor}
                    bookedEvent={clickedBookedSlotData}
                    closeFunction={() => setEditBookingDialogOpen(false)}
                    callbackFunctionAfterSave={() => {
                      setEditBookingDialogOpen(false)
                      getAndSetBookedSlotsOfSelectedDate()
                    }}
                    cancelAppointmentApiSignature={cancelAppointmentApiSignature}
                    editBookingApiSignature={editBookingApiSignature}
                    showRescheduleAction={isRescheduleVisibleForSlot(clickedBookedSlotData)}
                    onRescheduleBooking={() => {
                      setEditBookingDialogOpen(false)
                      openStandaloneReschedule(clickedBookedSlotData)
                    }}
                  />
                )}
                {rescheduleDialogType === 'support' ? (
                  <SupportBookedEventRescheduleDrawer
                    open={standaloneRescheduleOpen}
                    bookedEvent={standaloneRescheduleSlot}
                    onClose={closeStandaloneReschedule}
                    onSuccess={() => {
                      closeStandaloneReschedule()
                      getAndSetBookedSlotsOfSelectedDate()
                    }}
                  />
                ) : (
                  <RescheduleSlotDialog
                    open={standaloneRescheduleOpen}
                    onClose={closeStandaloneReschedule}
                    bookedSlot={standaloneRescheduleSlot}
                    refreshBookedSlots={getAndSetBookedSlotsOfSelectedDate}
                  />
                )}
                {/* New booking dialog is rendered once outside the per-tab markup
                    (see renderNewBookingDialog() near the end of the return). */}
                <TherapyBookingDialogComponent
                  open={therapyBookingDialogOpen}
                  clinicLocationId={selectedClinic?.id}
                  clinicBrandId={selectedClinic?.clinicBrandId}
                  clinicLocationData={selectedClinic}
                  handleCloseDialog={() => setTherapyBookingDialogOpen(false)}
                  data={dataForTherapyBookingDialog}
                  setFetchDataTriggerForBookingRequests={setFetchDataTriggerForBookingRequests}
                />
                {cancelDialogData && (
                  <ConfirmCancelSessionDialog
                    open={cancelDialogData}
                    closeFunc={() => setCancelDialogData(null)}
                    sessionData={cancelDialogData}
                    cancelAppointmentApiSignature={cancelAppointmentApiSignature}
                    afterCancelSuccess={getAndSetBookedSlotsOfSelectedDate}
                  />
                )}
                {markCompletedDialogData && (
                  <ConfirmMarkCompletedDialog
                    open={Boolean(markCompletedDialogData)}
                    onClose={() => setMarkCompletedDialogData(null)}
                    sessionData={markCompletedDialogData}
                    markBookedSessionAsCompletedFunction={markBookedSessionAsCompletedFunction}
                    refreshBookedSlots={getAndSetBookedSlotsOfSelectedDate}
                  />
                )}
              </div>
            )}
            {isViewingConsultsTab && (
              <>
              <ConsultsTab
                bookedEventsListWithPatientInfo={bookedSlotsInDay?.filter(
                  (bookedSlot) => selectedClinic?.id === bookedSlot?.clinicId,
                )}
                mentorProfileData={mentorProfileData}
                clinicLocationData={selectedClinic}
                handleNewTherapyRequest={handleNewTherapyRequest}
                setClickedBookedSlotData={setClickedBookedSlotData}
                // therapyBookingDataForThisEvent={therapyBookingDataForThisEvent}
                fileUploadApiSignature={fileUploadApiSignature}
                setEditPatientDialogOpen={setEditPatientDialogOpen}
                sendPrescriptionPdfApiSignature={sendPrescriptionPdfApiSignature}
                prescriptionGetApiSignature={getPrescriptionImagesApiSignature}
                prescriptionSaveApiSignature={savePrescriptionImagesApiSignature}
                showGalleryForPrescriptionImages={showGalleryForPrescriptionImages}
                sendSecureSessionLinkToPatientSignature={sendSecureSessionLinkToPatientSignature}
                sendPublicSessionLinkToPatientSignature={sendPublicSessionLinkToPatientSignature}
                prescriptionImagesGetApiSignature={getPrescriptionImagesApiSignature}
                prescriptionImagesSaveApiSignature={savePrescriptionImagesApiSignature}
                refreshBookedSlots={getAndSetBookedSlotsOfSelectedDate}
                prescriptionViewSettings={prescrpitionViewSettingsStoreData?.data}
                forMentor={forMentor}
                setAddVitalsDialogOpen={setAddVitalsDialogOpen}
                setEditBookingDialogOpen={setEditBookingDialogOpen}
                loadingTableData={bookedSlotsInDayLoading}
                showActionButtons
                viewButtonClick={forMentor ? goToEvent : undefined}
                hidePatientContactDetails={hidePatientContactDetails}
              />
              {/* Vitals / patient / booking dialogs for the Appointments-tab row actions */}
              {renderClickedSlotDialogs()}
              </>
            )}
            {isViewingPlannedTab && (
              <ScheduledPatientsTable
                clinicBrandIds={[thisClinicData?.clinicBrandId]}
                clinicIds={[thisClinicData?.id]}
                hideClinicDetails
                isEventTypesNotMandatory
                reportFetchingApiURL="/api/secure/teacher/get-scheduled-patients"
                fromInDayJsFormat={dayjs(selectedDateUtcStartTime)}
                toInDayJsFormat={dayjs(selectedDateUtcEndTime)}
              />
            )}
          </EDVCParent>
        </Box>

        {/* "Search or Create Patient" booking dialog — rendered once so it works on
            both the Calendar tab (empty-slot click) and the Appointments tab ("New"). */}
        {renderNewBookingDialog()}

        {/* Booked Slots Drawer */}
        {bookedSlotsDrawerOpen && (
          <Suspense fallback={<Box sx={{ p: 2, display: 'flex', justifyContent: 'center' }}><CircularProgress size={24} /></Box>}>
            <BookedSlotsDrawer
              open={bookedSlotsDrawerOpen}
              onClose={() => setBookedSlotsDrawerOpen(false)}
          bookedSlotsInDay={bookedSlotsInDay}
          selectedDate={selectedDate}
          selectedClinic={selectedClinic}
          clinicAssociations={clinicAssociations}
          forMentor={forMentor}
          goToEvent={goToEvent}
          markBookedSessionAsCompletedFunction={markBookedSessionAsCompletedFunction}
          getAndSetBookedSlotsOfSelectedDate={getAndSetBookedSlotsOfSelectedDate}
          setCancelDialogData={setCancelDialogData}
          setEditPatientDialogOpen={setEditPatientDialogOpen}
          setClickedBookedSlotData={setClickedBookedSlotData}
          setEditBookingDialogOpen={setEditBookingDialogOpen}
          setAddVitalsDialogOpen={setAddVitalsDialogOpen}
          setDataForTherapyBookingDialog={setDataForTherapyBookingDialog}
          setTherapyBookingDialogOpen={setTherapyBookingDialogOpen}
          handleNewTherapyRequest={handleNewTherapyRequest}
          pendingActionsOfPerson={pendingActionsOfPerson}
          platformOptions={platformOptions}
          bookedSlotActionText={bookedSlotActionText}
          hideStartButton={hideStartButton}
          hideMarkedCompletedButton={hideMarkedCompletedButton}
          canPersonPrintPrescription={canPersonPrintPrescription}
          openReceiptDialog={openReceiptDialog}
          setEventDataForReceiptPrinting={setDataForReceiptPrinting}
          hideRescheduleDialog={hideRescheduleDialog}
          canRescheduleBookedSlot={canRescheduleBookedSlot}
          rescheduleDialogType={rescheduleDialogType}
          hidePatientContactDetails={hidePatientContactDetails}
          openReportViewDrawer={(slot) => {
            setSelectedBookedSlotForReport(slot)
            setReportViewDrawerOpen(true)
          }}
          initialFilterType={selectedFilterType}
            />
          </Suspense>
        )}

        {/* Report View Drawer */}
        {reportViewDrawerOpen && (
          <Suspense fallback={<Box sx={{ p: 2, display: 'flex', justifyContent: 'center' }}><CircularProgress size={24} /></Box>}>
            <ReportViewDrawer
              open={reportViewDrawerOpen}
              onClose={() => {
                setReportViewDrawerOpen(false)
                setSelectedBookedSlotForReport(null)
              }}
              bookedSlot={selectedBookedSlotForReport}
            />
          </Suspense>
        )}

        {/* Booked Event Details Drawer (Support Portal and Mentor Portal) */}
        {(isSupportPortal || forMentor) && bookedEventDetailsDrawerOpen && (
          <Suspense fallback={<Box sx={{ p: 2, display: 'flex', justifyContent: 'center' }}><CircularProgress size={24} /></Box>}>
            <BookedEventDetailsDrawer
              open={bookedEventDetailsDrawerOpen}
              onClose={() => {
                setBookedEventDetailsDrawerOpen(false)
                setSelectedBookedSlotForDrawer(null)
              }}
              bookedSlot={selectedBookedSlotForDrawer}
              forMentor={forMentor}
              showViewReportsButton={showViewReportsButtonInActionButtons}
              showPrescriptionPdfDownloadButtons={shouldShowPrescriptionPdfDownloadButtons({
                forMentor,
                bookedSlot: selectedBookedSlotForDrawer,
                prescriptionViewSettingsData: prescrpitionViewSettingsStoreData?.data,
              })}
              forceShowJoiningDetails={forceShowJoiningDetailsActionButton}
              hideRescheduleDialog={hideRescheduleDialog}
              canRescheduleBookedSlot={canRescheduleBookedSlot}
              refreshBookedEvents={getAndSetBookedSlotsOfSelectedDate}
              sendSecureSessionLinkToPatientSignature={sendSecureSessionLinkToPatientSignature}
              sendPublicSessionLinkToPatientSignature={sendPublicSessionLinkToPatientSignature}
              showSendingLinkButtonsToSupport={showSendingLinkButtonsToSupport}
              clinicAssociations={clinicAssociations}
              // Action handlers
              onOpenJoiningDetails={() => drawerActionDialogs.joiningDetails.open()}
              onOpenEventLogs={() => drawerActionDialogs.bookedEventLogs.open()}
              onOpenReportView={() => {
                if (selectedBookedSlotForDrawer) {
                  setSelectedBookedSlotForReport(selectedBookedSlotForDrawer)
                  setReportViewDrawerOpen(true)
                }
              }}
              onOpenPrescriptionDownload={() => drawerPrescriptionDialogs.openOptionsDialog()}
              onOpenUploadPrescription={() => drawerActionDialogs.uploadPrescriptionImages.open()}
              onOpenPaymentLinks={() => drawerActionDialogs.paymentLinks.open()}
              onGoToEvent={goToEvent}
              onSendReportToDoctor={() => {
                toast.info('Send Report to Doctor functionality coming soon')
              }}
              onBookScreening={async () => {
                if (!selectedBookedSlotForDrawer) return
                try {
                  const response = await putCPDataWithoutParams(`/api/secure/meeting/screening/book`, {
                    ...selectedBookedSlotForDrawer,
                    apiParam: 'booked-event-info',
                  })
                  if (
                    response?.status === 200 &&
                    isCommonResponseSuccessful(response?.data?.code) &&
                    response?.data?.data?.screening
                  ) {
                    toast.success(
                      `Screening booked for ${response?.data?.data?.screening?.userName} with ${response?.data?.data?.screening?.mentorName}`,
                    )
                    getAndSetBookedSlotsOfSelectedDate()
                  } else {
                    toast.error('Error booking screening')
                  }
                } catch (error) {
                  toast.error('Error booking screening')
                }
              }}
              onSendForTherapy={() => drawerActionDialogs.sendTherapy.open()}
              onSendForAssessment={() => drawerActionDialogs.sendAssessment.open()}
              onOpenTreatmentHistory={() => drawerActionDialogs.patientBooking.open()}
              onBookTherapy={() => {
                const pendingActionsForThisBookedEvent = pendingActionsOfPerson?.filter(
                  (item) => item?.bookedEventId === selectedBookedSlotForDrawer?.id,
                )
                const therapyBookingDataForThisEvent = pendingActionsForThisBookedEvent?.find(
                  (pa) => pa?.type === 'schedule-therapy',
                )
                handleNewTherapyRequest(therapyBookingDataForThisEvent, selectedBookedSlotForDrawer)
              }}
              onRequestReassign={async () => {
                if (!selectedBookedSlotForDrawer) return
                try {
                  const response = await putCPDataWithoutParams(
                    `/api/secure/meeting/screening/auto-reassign-eligible`,
                    {
                      ...selectedBookedSlotForDrawer,
                      apiParam: 'booked-event-info',
                    },
                  )
                  if (response?.status === 200 && isCommonResponseSuccessful(response?.data?.code)) {
                    toast.success('Requested')
                    getAndSetBookedSlotsOfSelectedDate()
                  } else {
                    toast.error('Error')
                  }
                } catch (error) {
                  toast.error('Error')
                }
              }}
              onToggleRecording={async () => {
                if (!selectedBookedSlotForDrawer) return
                const loadingToast = toast.loading('Updating Recording Status...')
                const apiUrl = !selectedBookedSlotForDrawer?.recordingEnabled
                  ? '/api/secure/cc/support/events/mark-event-as-recordable'
                  : '/api/secure/cc/support/events/mark-event-as-not-recordable'

                const resp = await putSPDataWithParams(apiUrl, null, {
                  'booked-event-id': selectedBookedSlotForDrawer?.id,
                })

                if (resp.status === 200 && isCommonResponseSuccessfulV2(resp)) {
                  getAndSetBookedSlotsOfSelectedDate()
                  toast.update(loadingToast, {
                    render: 'Recording Status Updated',
                    type: 'success',
                    isLoading: false,
                    autoClose: 3000,
                  })
                } else {
                  toast.update(loadingToast, {
                    render: 'Failed to update recording status',
                    type: 'error',
                    isLoading: false,
                    autoClose: 3000,
                  })
                }
              }}
              onMarkCompleted={() => {
                if (selectedBookedSlotForDrawer) {
                  setMarkCompletedDialogData(selectedBookedSlotForDrawer)
                }
              }}
              onOpenPatientDetails={() => {
                setEditPatientDialogOpen(true)
              }}
              onAddVitals={() => {
                setAddVitalsDialogOpen(true)
              }}
              onEditBooking={() => {
                setEditBookingDialogOpen(true)
              }}
              onReschedule={() => {
                drawerActionDialogs.reschedule.open()
              }}
              setClickedBookedSlotData={setClickedBookedSlotData}
              handleNewTherapyRequest={handleNewTherapyRequest}
              therapyBookingDataForThisEvent={(() => {
                if (!selectedBookedSlotForDrawer) return undefined
                const pendingActionsForThisBookedEvent = pendingActionsOfPerson?.filter(
                  (item) => item?.bookedEventId === selectedBookedSlotForDrawer?.id,
                )
                return pendingActionsForThisBookedEvent?.find(
                  (pa) => pa?.type === 'schedule-therapy',
                )
              })()}
              openEmrImagesDialog={() => drawerActionDialogs.emrImages.open()}
              openReceiptDialog={openReceiptDialog}
              setEventDataForReceiptPrinting={setDataForReceiptPrinting}
            />

            {/* Dialogs for drawer actions */}
            {selectedBookedSlotForDrawer && (() => {
              const emrApiData = getEmrApiData(selectedBookedSlotForDrawer?.eventType)
              return (
                <BookedSlotDialogs
                  dialogs={drawerActionDialogs}
                  bookedSlot={selectedBookedSlotForDrawer}
                  mentorProfileData={mentorProfileData}
                  clinicLocationData={selectedClinic}
                  prescriptionGetApiSignature={getPrescriptionImagesApiSignature}
                  prescriptionSaveApiSignature={savePrescriptionImagesApiSignature}
                  fileUploadApiSignature={fileUploadApiSignature}
                  showGalleryForPrescriptionImages={showGalleryForPrescriptionImages}
                  sendSecureSessionLinkToPatientSignature={sendSecureSessionLinkToPatientSignature}
                  sendPublicSessionLinkToPatientSignature={sendPublicSessionLinkToPatientSignature}
                  prescriptionImagesGetApiSignature={getPrescriptionImagesApiSignature}
                  prescriptionImagesSaveApiSignature={savePrescriptionImagesApiSignature}
                  refreshBookedSlots={getAndSetBookedSlotsOfSelectedDate}
                  prescriptionDialogs={drawerPrescriptionDialogs}
                  calculateCreatedTimeFrom={(bookedSlot) => calculateCreatedTimeFrom(bookedSlot, selectedClinic)}
                  calculateCreatedTimeTo={(bookedSlot) => calculateCreatedTimeTo(bookedSlot, selectedClinic)}
                  showPatientHistoryInUploadPrescriptionWidget={showPatientHistoryInUploadPrescriptionWidget}
                  patientHistoryGetApiSignature={patientHistoryGetApiSignature}
                  emrImagesGetApiSignature={emrApiData?.getApiSignature || ''}
                  emrImagesSaveApiSignature={emrApiData?.saveApiSignature || ''}
                  showSendingLinkButtonsToSupport={showSendingLinkButtonsToSupport}
                  emrDialogTitle={emrApiData?.dialogTitle || 'Upload EMR Images'}
                  emrImagesUploadImagesSignature={emrApiData?.uploadImagesSignature || ''}
                  rescheduleDialogType={rescheduleDialogType}
                />
              )
            })()}
          </Suspense>
        )}
      </>
    )
  },
)

export default DayViewCalendarComponent

// Re-export utility functions for backward compatibility
export { getBookedSlotStripColor, getBookedSlotStripColorForRoster } from './utils/bookedSlotColorUtils'
